/* eslint-disable max-lines */
import type { Entity } from '@ms/yammer-data-support';
import { moveToFulfilled, moveToPending, moveToRejected, remove } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import { getGraphqlApiErrorCode, getGraphqlApiErrorReason } from '../..';
import type { UpdateUserAdminRolesAction } from '../../domains/adminRoles/actions.types';
import type {
  CampaignsListLoadMoreFulfilledAction,
  CampaignsListLoadMorePendingAction,
  CampaignsListLoadMoreRejectedAction,
} from '../../domains/allCampaigns/loadMoreAllCampaignsActions.types';
import type { AllNetworkGroupsLoadMoreAction } from '../../domains/allNetworkGroups/actions.types';
import type { AppViewPropertiesLoadAction } from '../../domains/appViewProperties/actions.types';
import { getAppViewPropertyRequestStatusId } from '../../domains/appViewProperties/getAppViewPropertyRequestStatusId';
import type { AppViewPropertiesSetAction } from '../../domains/appViewProperties/setActions.types';
import type { ArchiveTeamsMeetingFeedAction } from '../../domains/archiveTeamsMeetingFeed/archiveTeamsMeetingFeedActions.types';
import type {
  ThreadArchiveFromTeamsMeetingFeedAction,
  ThreadUnarchiveFromTeamsMeetingFeedAction,
} from '../../domains/archiveThreads/archiveThreadActions.types';
import type { CampaignCoverImageDeleteAction } from '../../domains/campaignCoverImage/deleteActions.types';
import type { CampaignCoverImageUploadAction } from '../../domains/campaignCoverImage/uploadActions.types';
import type { CampaignDeleteAction } from '../../domains/campaignDeletion/actions.types';
import type { CampaignEditAction, CampaignPublishAction } from '../../domains/campaignEdit/actions.types';
import type { CampaignFollowAction } from '../../domains/campaignFollowing/followActions.types';
import type { CampaignUnfollowAction } from '../../domains/campaignFollowing/unfollowActions.types';
import type {
  CampaignGoalTrackerCreateAction,
  CampaignGoalTrackerEditAction,
} from '../../domains/campaignGoalTracker/actions.types';
import type { CampaignSashAddAction } from '../../domains/campaignSash/addSashActions.types';
import type { CampaignSashRemoveAction } from '../../domains/campaignSash/removeSashActions.types';
import type { CampaignLoadAction } from '../../domains/campaigns/actions.types';
import type { CancelEventAction, CancelEventFulfilledAction } from '../../domains/cancelEvent/actions.types';
import type { OfficeTopicLoadAuthorizedAction } from '../../domains/cortexTopics/actions.types';
import type { CreateCalendarInviteForTeamsMeetingAction } from '../../domains/createCalendarInviteForTeamsMeeting/actions.types';
import type { CreateGroupCalendarInviteForBroadcastAction } from '../../domains/createGroupCalendarInviteForBroadcast/actions.types';
import type { CreateTeamsMeetingAction } from '../../domains/createTeamsMeeting/actions.types';
import type { CancelGenerateCuratedThreadsAction } from '../../domains/curatedThreadList/actions.types';
import type { EditUserDelegateAction } from '../../domains/delegation/actions.types';
import type { AddUserDelegateAction, RemoveUserDelegateAction } from '../../domains/delegationAddRemove/actions.types';
import type { DeleteDraftThreadsAction } from '../../domains/deleteDraftThreads/actions.types';
import { deleteDraftThreadsRequestStatusId } from '../../domains/deleteDraftThreads/actions.types';
import type { DeleteFileAction } from '../../domains/deleteFile/actions.types';
import type { DeleteMessageAction } from '../../domains/deleteMessage/actions.types';
import type { DeleteTeamsMeetingAction } from '../../domains/deleteTeamsMeeting/actions.types';
import type { DeleteThreadAction } from '../../domains/deleteThread/actions.types';
import type { EditEventAction, EditRestrictedPostingAction } from '../../domains/editEvent/actions.types';
import type { EditTeamsMeetingAction } from '../../domains/editTeamsMeeting/actions.types';
import type { EditTenantConfigurationSettingsAction } from '../../domains/editTenantConfigurationSettings/actions.types';
import type { TopicsAllLoadAction, TopicsAllLoadMoreAction } from '../../domains/engageTopicsAll/actions.types';
import type { EventLoadByBroadcastIdAction } from '../../domains/eventLoadByBroadcastId/actions.types';
import type { EditNetworkQuestionSettingsAction } from '../../domains/featureAccessSettings/editNetworkQuestionSettingsActions.types';
import type { FeedCountsLoadGroupAction } from '../../domains/feedCounts/actions.types';
import type { FeedLoadNewerThreadsAction, FeedLoadOlderThreadsAction } from '../../domains/feedLoad/actions.types';
import type { FeedMarkAllGroupMessagesAsSeenAction } from '../../domains/feeds/actions.types';
import type { AddGroupLeaderAction, RemoveGroupLeaderAction } from '../../domains/groupAddRemoveLeader/action.types';
import type {
  GroupAddUserAction,
  GroupAddUserByEmailAction,
  GroupRemoveUserAction,
} from '../../domains/groupAddRemoveUser/actions.types';
import type { GroupAvatarUploadAction } from '../../domains/groupAvatarUploads/actions.types';
import type {
  GroupCoverImageDeleteAction,
  GroupCoverImageUploadAction,
} from '../../domains/groupCoverImages/actions.types';
import type { DeleteGroupActions } from '../../domains/groupDeletion/actions.types';
import type {
  GroupDiscoverySettingsLoadAction,
  GroupHideForNetworkInDiscoveryAction,
  GroupHideForViewerInDiscoveryAction,
  GroupUnhideForNetworkInDiscoveryAction,
  GroupUnhideForViewerInDiscoveryAction,
} from '../../domains/groupDiscoverySettings/actions.types';
import type { GroupEventsSidebarLoadAction } from '../../domains/groupEventsSidebarLoad/actions.types';
import type { GroupMakeAdminAction, GroupRevokeAdminAction } from '../../domains/groupMakeRevokeAdmin/actions.types';
import type {
  GroupMemberPanelsDismissActionErrorsAction,
  GroupMemberPanelsLoadMoreAction,
} from '../../domains/groupMemberPanels/actions.types';
import type { GroupMembersBulkUploadAction } from '../../domains/groupMembersBulkUpload/actions.types';
import type { ResolveGroupMembershipAction } from '../../domains/groupResolveMembershipRequest/actions.types';
import type { GroupSidebarLoadFulfilledAction } from '../../domains/groupSidebarLoad/actions.types';
import type { GroupEditActions } from '../../domains/groups/actions.types';
import type { ThreadHideFromCampaignFeedAction } from '../../domains/hiddenThreads/campaignActions.types';
import type { UserHideUserAction } from '../../domains/hideUsers/hideUserActions.types';
import type { UserUnhideUserAction } from '../../domains/hideUsers/unhideUserActions.types';
import type { InboxUnreadCountLoadAction } from '../../domains/inboxUnreadCountLoad/actions.types';
import type {
  InformationBarrierSettingsEditAction,
  InformationBarrierSettingsEditFulfilledAction,
} from '../../domains/informationBarrierSettings/actions.types';
import type {
  AudienceMembersLoadAction,
  CreateAudienceAction,
  CreateAudienceFromDirectMembersAction,
  CreateAudienceFromOfficeGroupAction,
  DesignateLeadersFromOfficeGroupAsyncAction,
  EditAudienceAction,
  LeaderLoadFulfilledAction,
  MarkUserDefaultAudienceAction,
  RemoveLeaderAudienceAction,
  UpdateHasMultiTenantWideAudienceAction,
  UpdateHasNetworkWideAudienceAction,
  UserConnectedLeadersListLoadMoreAction,
  UserFollowedLeadersListLoadMoreAction,
  UserNetworkLeadersListLoadFulfilledAction,
  UserNetworkLeadersListLoadMoreAction,
  UserNetworkLeadersListLoadMoreFulfilledAction,
} from '../../domains/leadersManagement/actions.types';
import type { DesignateLeaderAction } from '../../domains/leadersManagement/designateLeaderActions.types';
import type {
  RemoveFromDirectAudienceAction,
  RemoveFromDirectAudienceFulfilledAction,
} from '../../domains/leadersManagement/removeFromDirectAudienceActions.types';
import type { RevokeLeaderAction } from '../../domains/leadersManagement/revokeLeaderActions.types';
import type { LiveEventDownloadIcsFileAction } from '../../domains/liveEventDownloadIcsFile/actions.types';
import type { LiveEventsLoadByNetworkAction } from '../../domains/liveEventsLoadByNetwork/actions.types';
import type {
  M365CopilotSuggestedMembersAddAllFulfilledAction,
  M365CopilotSuggestedMembersAddAllPendingAction,
  M365CopilotSuggestedMembersAddAllRejectedAction,
} from '../../domains/m365CopilotGroupSuggestedMembers/actions.types';
import type { MessageUpvotesLoadMoreAction } from '../../domains/messageUpvoteLoad/paginateActions.types';
import type {
  DismissModeratedThreadAction,
  PublishModeratedThreadAction,
  RestoreModeratedThreadAction,
} from '../../domains/messageWithModerationState/actions.types';
import type {
  GroupMarkAsMultiTenantOrganizationGroupAction,
  GroupUnmarkAsMultiTenantOrganizationGroupAction,
} from '../../domains/multiTenantOrganizationGroup/actions.types';
import type { MuteThreadAction, UnmuteThreadAction } from '../../domains/muteThread/actions.types';
import type { MyGroupsLoadMoreAction } from '../../domains/myGroups/actions.types';
import type { NestedThreadListLoadAction } from '../../domains/nestedThreadList/actions.types';
import type {
  GroupMarkAsOfficialAction,
  GroupUnmarkAsOfficialAction,
  OfficialGroupsLoadMoreAction,
} from '../../domains/officialGroups/actions.types';
import type {
  EditPinnedLinkTitleFulfilledAction,
  EditPinnedLinkTitlePendingAction,
  EditPinnedLinkTitleRejectedAction,
  PinCampaignObjectFulfilledAction,
  PinCampaignObjectPendingAction,
  PinCampaignObjectRejectedAction,
  PinObjectFulfilledAction,
  PinObjectPendingAction,
  PinObjectRejectedAction,
} from '../../domains/pinnedResources/actions.types';
import type {
  ThreadPinToCampaignFeedAction,
  ThreadPinToEventFeedAction,
  ThreadPinToGroupFeedAction,
  ThreadPinToMyLeadersFeedAction,
  ThreadPinToTeamsMeetingFeedAction,
  ThreadPinToUserStorylineFeedAction,
  ThreadUnpinFromCampaignFeedAction,
  ThreadUnpinFromEventFeedAction,
  ThreadUnpinFromGroupFeedAction,
  ThreadUnpinFromMyLeadersFeedAction,
  ThreadUnpinFromTeamsMeetingFeedAction,
  ThreadUnpinFromUserStorylineFeedAction,
} from '../../domains/pinnedThreads/actions.types';
import type { PollCloseAction, PollReopenAction } from '../../domains/pollClose/actions.types';
import type { PollLoadAction, PollVoteAction } from '../../domains/polls/actions.types';
import type { PromoteThreadAction, UnpromoteThreadAction } from '../../domains/promoteThread/types';
import type {
  PublishDirectMessageAction,
  PublishEditMessageAction,
  PublishEventMessageAction,
  PublishGroupMessageAction,
  PublishNetworkQuestionMessageAction,
  PublishNonReduxAction,
  PublishPendingAction,
  PublishPollMessageAction,
  PublishPraiseMessageAction,
  PublishReplyMessageAction,
  PublishTeamsMeetingMessageAction,
  PublishUserMessageAction,
  PublishUserPollMessageAction,
  PublishUserPraiseMessageAction,
  PublishersRemoveAction,
  PublishersRemovePublishMessageRequestAction,
} from '../../domains/publishers/actions.types';
import type { ReportMessageAction } from '../../domains/reportMessage/reportActions.types';
import { getId } from '../../domains/requestStatuses/getId';
import type { ResetAction } from '../../domains/requestStatuses/types';
import type { ResolveWebLinkAction } from '../../domains/resolveWebLink/actions.types';
import type { RoleBasedAction } from '../../domains/roleBasedFREs/actions.types';
import type { SearchLoadMoreAction } from '../../domains/searches/actions.types';
import type {
  SecondLevelRepliesLoadLastAction,
  SecondLevelRepliesLoadNextAction,
  SecondLevelRepliesLoadPreviousAction,
} from '../../domains/secondLevelReplies/actions.types';
import type { SessionEditCurrentNetworkUsagePolicyAction } from '../../domains/sessionEditCurrentNetworkUsagePolicy/actions.types';
import type { UpdateSessionReactionAccentColorAction } from '../../domains/sessionReactionAccentColor/actions.types';
import type { CheckSharePointVideoAccessAction } from '../../domains/sharePointVideoLinks/actions.types';
import type { SharePointVideoPreviewUrlLoadAction } from '../../domains/sharePointVideoPreviewUrls/actions.types';
import type {
  StorylineMtoAdminSettingsUpdateAction,
  StorylinePerUserControlSettingsUpdateSettingAction,
} from '../../domains/storylineAdminSettings/actions.types';
import type { UpdateStorylineDefaultNotificationDeliverySettingsAction } from '../../domains/storylineAdminSettings/updateDefaultNotificationDeliverySettingsActions.types';
import type { SuggestedTopicsLoadMoreAction } from '../../domains/suggestedTopics/loadMoreActions.types';
import type { TeamsMeetingLoadAction } from '../../domains/teamsMeetingLoad/actions.types';
import type { ThreadBookmarkAction, ThreadUnbookmarkAction } from '../../domains/threadBookmark/types';
import type { ThreadCloseAction, ThreadReopenAction } from '../../domains/threadClose/actions.types';
import type { CollaboratorUpdateAction } from '../../domains/threadCollaboratorUpdate/actions.types';
import type {
  ThreadMarkAsQuestionAction,
  ThreadUnmarkAsQuestionAction,
} from '../../domains/threadMarkAsQuestion/types';
import type { ThreadMoveAction } from '../../domains/threadMove/actions.types';
import type { RemoveThreadParticipantsAction } from '../../domains/threadRemoveParticipants/actions.types';
import type { ThreadSharesLoadAction } from '../../domains/threadShares/actions.types';
import type { ThreadsAppliedTopicNamesLoadAction } from '../../domains/threadsAppliedTopicNames/actions.types';
import type {
  TopLevelRepliesLoadInitialAction,
  TopLevelRepliesLoadLastAction,
  TopLevelRepliesLoadNextAction,
  TopLevelRepliesLoadPreviousAction,
} from '../../domains/topLevelReplies/actions.types';
import type {
  TopicFollowersLoadMoreFulfilledAction,
  TopicFollowersLoadMorePendingAction,
  TopicFollowersLoadMoreRejectedAction,
} from '../../domains/topicFollowers/paginateActions.types';
import type {
  AddThreadTopicActions,
  RemoveThreadTopicActions,
  RemoveTopicAction,
  TopicUncurateAction,
} from '../../domains/topics/actions.types';
import type { TopicEditAction } from '../../domains/topics/editActions.types';
import type { TopicsFollowedLoadMoreAction } from '../../domains/topicsFollowedLoad/loadMoreActions.types';
import type { TrendingTopicsLoadMoreAction } from '../../domains/trendingTopicsLoad/loadMoreActions.types';
import type { TypeaheadFulfilledAction } from '../../domains/typeahead/actions.types';
import type { UnseenThreadsLoadAction } from '../../domains/unseenThreadsLoad/actions.types';
import type { UploadTenantLogoAction } from '../../domains/uploadTenantLogo/actions.types';
import type { EditUserAchievementAdminSettingsAction } from '../../domains/userAchievementsSettings/editUserAchievementAdminSettingsActions.types';
import type { EditUserAchievementUserSettingsAction } from '../../domains/userAchievementsSettings/editUserAchievementUserSettingsActions.types';
import type {
  UserCoverImageDeleteAction,
  UserCoverImageUploadAction,
} from '../../domains/userCoverImages/actions.types';
import type { UpdateUserPreferencesAction } from '../../domains/userPreferences/actions.types';
import type { AddFavoriteCommunityRejectedAction } from '../../domains/viewerCommunityRelationships/actions.types';
import type { TeamsMeetingsByOrganizerAction } from '../teamsMeetingsByOrganizer/reducer';
import type { UserInformationBarrierSettingsActions } from '../userInformationBarrierSettings/reducer';

import type { RequestStatusType, RequestStatuses } from './types';
import {
  addFavoriteGroupRequestId,
  addUserDelegateRequestId,
  editInformationBarrierAdminSettingsRequestId,
  editNetworkQuestionsSettingsRequestId,
  editNetworkUsagePolicyRequestId,
  editTenantConfigurationSettingsRequestId,
  editUserDelegateRequestId,
  inboxUnreadCountRequestId,
  liveEventsLoadByNetworkRequestId,
  officialGroupsLoadMoreRequestId,
  removeUserDelegateRequestId,
  suggestedTopicsLoadMoreRequestId,
  tenantConfigurationAndRolesRequestId,
  topicsAllLoadMoreRequestId,
  topicsAllLoadRequestId,
  updateReactionAccentColorRequestId,
  updateStorylineDefaultNotificationDeliverySettingsRequestId,
  updateStorylineMtoAdminSettingsRequestId,
  updateStorylinePerUserControlSettingsRequestId,
  uploadTenantLogoRequestId,
} from './types';

type GroupPinnedResourceActions =
  | EditPinnedLinkTitleFulfilledAction
  | EditPinnedLinkTitlePendingAction
  | GroupSidebarLoadFulfilledAction
  | PinObjectFulfilledAction
  | PinObjectRejectedAction;

type CampaignPinnedResourceActions =
  | PinCampaignObjectPendingAction
  | PinCampaignObjectFulfilledAction
  | PinCampaignObjectRejectedAction;

type CampaignsListLoadActions =
  | CampaignsListLoadMoreFulfilledAction
  | CampaignsListLoadMorePendingAction
  | CampaignsListLoadMoreRejectedAction;

type PublishMessageRequestAction =
  | PublishGroupMessageAction
  | PublishEventMessageAction
  | PublishUserMessageAction
  | PublishUserPollMessageAction
  | PublishUserPraiseMessageAction
  | PublishDirectMessageAction
  | PublishEditMessageAction
  | PublishReplyMessageAction
  | PublishPollMessageAction
  | PublishPraiseMessageAction
  | PublishTeamsMeetingMessageAction
  | PublishNetworkQuestionMessageAction
  | PublishPendingAction
  | PublishNonReduxAction;

type PinnedResourceModalActions = EditPinnedLinkTitleRejectedAction | PinObjectPendingAction;

type LeaderLoadAction =
  | TypeaheadFulfilledAction
  | UserNetworkLeadersListLoadFulfilledAction
  | UserNetworkLeadersListLoadMoreFulfilledAction
  | LeaderLoadFulfilledAction;

type TopicFollowersLoadAction =
  | TopicFollowersLoadMorePendingAction
  | TopicFollowersLoadMoreFulfilledAction
  | TopicFollowersLoadMoreRejectedAction;

export type RequestStatusAction =
  | AddFavoriteCommunityRejectedAction
  | AddGroupLeaderAction
  | AddThreadTopicActions
  | AddUserDelegateAction
  | AllNetworkGroupsLoadMoreAction
  | AppViewPropertiesLoadAction
  | AppViewPropertiesSetAction
  | ArchiveTeamsMeetingFeedAction
  | AudienceMembersLoadAction
  | CampaignLoadAction
  | CampaignPinnedResourceActions
  | CampaignsListLoadActions
  | CampaignFollowAction
  | CampaignUnfollowAction
  | CampaignGoalTrackerCreateAction
  | CampaignGoalTrackerEditAction
  | CampaignSashAddAction
  | CampaignSashRemoveAction
  | CancelEventAction
  | CancelGenerateCuratedThreadsAction
  | CollaboratorUpdateAction
  | CreateAudienceAction
  | CreateAudienceFromDirectMembersAction
  | CreateAudienceFromOfficeGroupAction
  | CreateCalendarInviteForTeamsMeetingAction
  | CreateGroupCalendarInviteForBroadcastAction
  | CreateTeamsMeetingAction
  | DeleteTeamsMeetingAction
  | DeleteDraftThreadsAction
  | DeleteFileAction
  | DeleteGroupActions
  | DeleteMessageAction
  | DeleteThreadAction
  | DesignateLeaderAction
  | DesignateLeadersFromOfficeGroupAsyncAction
  | DismissModeratedThreadAction
  | EditAudienceAction
  | EditEventAction
  | EditNetworkQuestionSettingsAction
  | EditRestrictedPostingAction
  | EditTeamsMeetingAction
  | EditTenantConfigurationSettingsAction
  | EditUserAchievementAdminSettingsAction
  | EditUserAchievementUserSettingsAction
  | EditUserDelegateAction
  | EventLoadByBroadcastIdAction
  | FeedCountsLoadGroupAction
  | FeedLoadNewerThreadsAction
  | FeedLoadOlderThreadsAction
  | FeedMarkAllGroupMessagesAsSeenAction
  | GroupAddUserAction
  | GroupAddUserByEmailAction
  | GroupAvatarUploadAction
  | GroupCoverImageDeleteAction
  | GroupCoverImageUploadAction
  | GroupDiscoverySettingsLoadAction
  | GroupEditActions
  | GroupEventsSidebarLoadAction
  | GroupHideForNetworkInDiscoveryAction
  | GroupHideForViewerInDiscoveryAction
  | GroupMakeAdminAction
  | GroupMarkAsOfficialAction
  | GroupMemberPanelsDismissActionErrorsAction
  | GroupMemberPanelsLoadMoreAction
  | GroupMembersBulkUploadAction
  | GroupPinnedResourceActions
  | GroupRemoveUserAction
  | GroupRevokeAdminAction
  | GroupUnhideForNetworkInDiscoveryAction
  | GroupUnhideForViewerInDiscoveryAction
  | GroupUnmarkAsOfficialAction
  | UserHideUserAction
  | UserUnhideUserAction
  | InboxUnreadCountLoadAction
  | InformationBarrierSettingsEditAction
  | InformationBarrierSettingsEditFulfilledAction
  | UserInformationBarrierSettingsActions
  | LeaderLoadAction
  | LiveEventDownloadIcsFileAction
  | LiveEventsLoadByNetworkAction
  | MarkUserDefaultAudienceAction
  | MessageUpvotesLoadMoreAction
  | MuteThreadAction
  | MyGroupsLoadMoreAction
  | NestedThreadListLoadAction
  | OfficeTopicLoadAuthorizedAction
  | OfficialGroupsLoadMoreAction
  | PinnedResourceModalActions
  | PollCloseAction
  | PollReopenAction
  | PollLoadAction
  | PollVoteAction
  | PromoteThreadAction
  | PublishersRemoveAction
  | PublishersRemovePublishMessageRequestAction
  | PublishMessageRequestAction
  | PublishModeratedThreadAction
  | RemoveFromDirectAudienceAction
  | RemoveGroupLeaderAction
  | RemoveLeaderAudienceAction
  | RemoveThreadParticipantsAction
  | RemoveThreadTopicActions
  | RemoveUserDelegateAction
  | RevokeLeaderAction
  | ReportMessageAction
  | ResetAction
  | ResolveGroupMembershipAction
  | ResolveWebLinkAction
  | RestoreModeratedThreadAction
  | SearchLoadMoreAction
  | SecondLevelRepliesLoadLastAction
  | SecondLevelRepliesLoadNextAction
  | SecondLevelRepliesLoadPreviousAction
  | SharePointVideoPreviewUrlLoadAction
  | CheckSharePointVideoAccessAction
  | SuggestedTopicsLoadMoreAction
  | SessionEditCurrentNetworkUsagePolicyAction
  | StorylineMtoAdminSettingsUpdateAction
  | TeamsMeetingLoadAction
  | TeamsMeetingsByOrganizerAction
  | ThreadBookmarkAction
  | ThreadCloseAction
  | ThreadArchiveFromTeamsMeetingFeedAction
  | ThreadHideFromCampaignFeedAction
  | ThreadMarkAsQuestionAction
  | ThreadMoveAction
  | ThreadPinToCampaignFeedAction
  | ThreadPinToEventFeedAction
  | ThreadPinToGroupFeedAction
  | ThreadPinToMyLeadersFeedAction
  | ThreadPinToTeamsMeetingFeedAction
  | ThreadPinToUserStorylineFeedAction
  | ThreadReopenAction
  | ThreadsAppliedTopicNamesLoadAction
  | ThreadSharesLoadAction
  | ThreadUnarchiveFromTeamsMeetingFeedAction
  | ThreadUnbookmarkAction
  | ThreadUnmarkAsQuestionAction
  | ThreadUnpinFromCampaignFeedAction
  | ThreadUnpinFromEventFeedAction
  | ThreadUnpinFromGroupFeedAction
  | ThreadUnpinFromMyLeadersFeedAction
  | ThreadUnpinFromTeamsMeetingFeedAction
  | ThreadUnpinFromUserStorylineFeedAction
  | TopicEditAction
  | TopicsAllLoadAction
  | TopicsAllLoadMoreAction
  | TopLevelRepliesLoadInitialAction
  | TopicUncurateAction
  | TopLevelRepliesLoadLastAction
  | TopLevelRepliesLoadNextAction
  | TopLevelRepliesLoadPreviousAction
  | TopicsFollowedLoadMoreAction
  | TrendingTopicsLoadMoreAction
  | UnmuteThreadAction
  | UnpromoteThreadAction
  | UnseenThreadsLoadAction
  | UploadTenantLogoAction
  | UpdateHasNetworkWideAudienceAction
  | UpdateHasMultiTenantWideAudienceAction
  | UpdateSessionReactionAccentColorAction
  | UserCoverImageDeleteAction
  | UserCoverImageUploadAction
  | CampaignCoverImageDeleteAction
  | CampaignCoverImageUploadAction
  | CampaignEditAction
  | CampaignPublishAction
  | CampaignDeleteAction
  | UserNetworkLeadersListLoadMoreAction
  | UserConnectedLeadersListLoadMoreAction
  | UserFollowedLeadersListLoadMoreAction
  | UpdateUserAdminRolesAction
  | UpdateStorylineDefaultNotificationDeliverySettingsAction
  | StorylinePerUserControlSettingsUpdateSettingAction
  | RemoveTopicAction
  | GroupMarkAsMultiTenantOrganizationGroupAction
  | GroupUnmarkAsMultiTenantOrganizationGroupAction
  | RoleBasedAction
  | M365CopilotSuggestedMembersAddAllPendingAction
  | M365CopilotSuggestedMembersAddAllFulfilledAction
  | M365CopilotSuggestedMembersAddAllRejectedAction
  | UpdateUserPreferencesAction
  | TopicFollowersLoadAction;

const initialState: RequestStatuses = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const moveRequestToPending = (
  state: RequestStatuses,
  type: RequestStatusType,
  entityId: Entity['id']
): RequestStatuses => moveToPending(state, [getId(type, entityId)]);

const moveRequestToFulfilled = (
  state: RequestStatuses,
  type: RequestStatusType,
  entityId: Entity['id']
): RequestStatuses => {
  const id = getId(type, entityId);

  return moveToFulfilled(state, { [id]: { id, fulfilledTime: new Date().getTime() } });
};

const moveRequestToRejected = (
  state: RequestStatuses,
  type: RequestStatusType,
  entityId: Entity['id'],
  error: unknown
): RequestStatuses => moveToRejected(state, [getId(type, entityId)], error);

type RemoveRequest = (state: RequestStatuses, type: RequestStatusType, entityId: Entity['id']) => RequestStatuses;
const removeRequest: RemoveRequest = (state, type, entityId) => remove(state, [getId(type, entityId)]);

const groupMemberPanelRelatedActions: RequestStatusType[] = [
  'GroupAddUser',
  'GroupAddUserByEmail',
  'GroupRemoveUser',
  'GroupMakeAdmin',
  'GroupRevokeAdmin',
  'GroupRevokeAdmin',
  'GroupResolveMembership',
  'GroupMembersBulkUpload',
];
const moveRequestToPendingAndRemoveRelatedRequestTypes = (
  state: RequestStatuses,
  type: RequestStatusType,
  relatedTypes: RequestStatusType[],
  entityId: Entity['id']
): RequestStatuses => {
  const stateWithoutRelatedActions = removeRelatedRequestTypes(state, relatedTypes, entityId);

  return moveToPending(stateWithoutRelatedActions, [getId(type, entityId)]);
};

const removeRelatedRequestTypes = (
  state: RequestStatuses,
  relatedTypes: RequestStatusType[],
  entityId: Entity['id']
): RequestStatuses =>
  relatedTypes.reduce((newState, relatedType) => removeRequest(newState, relatedType, entityId), state);

interface MoveRequestToPendingAndRemoveAssociatedRequestArgs {
  readonly pendingType: RequestStatusType;
  readonly removeType: RequestStatusType;
  readonly entityId: Entity['id'];
}

const moveRequestToPendingAndRemoveAssociatedRequest = (
  state: RequestStatuses,
  { pendingType, removeType, entityId }: MoveRequestToPendingAndRemoveAssociatedRequestArgs
) => {
  const stateWithoutAssociatedRequest = removeRequest(state, removeType, entityId);

  return moveRequestToPending(stateWithoutAssociatedRequest, pendingType, entityId);
};

const stateWithCancelledEvent = (state: RequestStatuses, { payload: { broadcastId } }: CancelEventFulfilledAction) => {
  const stateWithFulfilledCancelledEventRequest = moveRequestToFulfilled(state, 'CancelEvent', broadcastId);

  return removeRequest(stateWithFulfilledCancelledEventRequest, 'EventLoadByBroadcastId', broadcastId);
};

const stateWithRemovedFromDirectAudience = (
  state: RequestStatuses,
  { payload: { userId } }: RemoveFromDirectAudienceFulfilledAction
) => {
  const stateWithFulfilledRemovedFromDirectAudienceRequest = moveRequestToFulfilled(
    state,
    'RemoveFromDirectAudience',
    userId
  );

  return removeRequest(stateWithFulfilledRemovedFromDirectAudienceRequest, 'RemoveFromDirectAudience', userId);
};

const removeRevokeLeaderRequestsForLoadedLeaders = (
  state: RequestStatuses,
  {
    payload: {
      entities: { userNetworkLeaders },
    },
  }: LeaderLoadAction
) => {
  if (userNetworkLeaders === undefined) {
    return state;
  }

  const requestIds = Object.keys(userNetworkLeaders)
    .filter((key) => userNetworkLeaders[key].isLeader)
    .map((entityId) => getId('RevokeLeader', entityId));

  return remove(state, requestIds);
};

// eslint-disable-next-line max-lines-per-function
const reducer: Reducer<RequestStatuses, RequestStatusAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'REQUEST_STATUSES.RESET':
      return removeRequest(state, action.payload.type, action.payload.entityId);
    case 'ALL_NETWORK_GROUPS.LOAD_MORE_PENDING':
      return moveRequestToPending(state, 'AllNetworkGroupsLoadMore', action.payload.sortBy);
    case 'ALL_NETWORK_GROUPS.LOAD_MORE_FULFILLED':
      return removeRequest(state, 'AllNetworkGroupsLoadMore', action.payload.sortBy);
    case 'ALL_NETWORK_GROUPS.LOAD_MORE_REJECTED':
      return moveRequestToRejected(state, 'AllNetworkGroupsLoadMore', action.payload.sortBy, action.payload.error);

    case 'APP_VIEW_PROPERTIES.LOAD_PENDING':
      return moveRequestToPending(
        state,
        'AppViewProperties',
        getAppViewPropertyRequestStatusId(action.payload.entityId, action.payload.propertyKeys)
      );
    case 'APP_VIEW_PROPERTIES.LOAD_FULFILLED':
      return removeRequest(
        state,
        'AppViewProperties',
        getAppViewPropertyRequestStatusId(action.payload.entityId, action.payload.propertyKeys)
      );
    case 'APP_VIEW_PROPERTIES.LOAD_REJECTED':
      return moveRequestToRejected(
        state,
        'AppViewProperties',
        getAppViewPropertyRequestStatusId(action.payload.entityId, action.payload.propertyKeys),
        action.payload.error
      );

    case 'APP_VIEW_PROPERTIES.SET_PENDING':
      return moveRequestToPending(
        state,
        'AppViewPropertiesSet',
        getAppViewPropertyRequestStatusId(action.payload.entityId, [action.payload.propertyKey])
      );
    case 'APP_VIEW_PROPERTIES.SET_FULFILLED':
      return removeRequest(
        state,
        'AppViewPropertiesSet',
        getAppViewPropertyRequestStatusId(action.payload.entityId, [action.payload.propertyKey])
      );
    case 'APP_VIEW_PROPERTIES.SET_REJECTED':
      return moveRequestToRejected(
        state,
        'AppViewPropertiesSet',
        getAppViewPropertyRequestStatusId(action.payload.entityId, [action.payload.propertyKey]),
        action.payload.error
      );

    case 'AUDIENCE_MEMBERS.LOAD_PENDING':
      return moveRequestToPending(state, 'AudienceMembersLoad', action.payload.audienceId);
    case 'AUDIENCE_MEMBERS.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'AudienceMembersLoad', action.payload.audienceId);
    case 'AUDIENCE_MEMBERS.LOAD_REJECTED':
      return moveRequestToRejected(state, 'AudienceMembersLoad', action.payload.audienceId, action.payload.error);

    case 'CAMPAIGN.SASH.ADD_PENDING':
      return moveRequestToPending(state, 'CampaignSash', action.payload.campaignId);
    case 'CAMPAIGN.SASH.ADD_FULFILLED':
      return moveRequestToFulfilled(
        state,
        'CampaignSash',
        action.payload.entities.userSashes[action.payload.userId].campaignId
      );
    case 'CAMPAIGN.SASH.ADD_REJECTED':
      return moveRequestToRejected(state, 'CampaignSash', action.payload.campaignId, action.payload.error);
    case 'CAMPAIGN.LOAD_PENDING':
      return moveRequestToPending(state, 'Campaign', action.payload.id);
    case 'CAMPAIGN.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'Campaign', action.payload.id);
    case 'CAMPAIGN.LOAD_REJECTED':
      return moveRequestToRejected(state, 'Campaign', action.payload.id, action.payload.error);
    case 'CAMPAIGN.SASH.REMOVE_PENDING':
      return moveRequestToPending(state, 'CampaignSash', action.payload.campaignId);
    case 'CAMPAIGN.SASH.REMOVE_FULFILLED':
      return moveRequestToFulfilled(state, 'CampaignSash', action.payload.campaignId);
    case 'CAMPAIGN.SASH.REMOVE_REJECTED':
      return moveRequestToRejected(state, 'CampaignSash', action.payload.campaignId, action.payload.error);

    case 'COLLABORATORS.UPDATE_FULFILLED':
      return moveRequestToFulfilled(state, 'UpdateCollaboratorsForDraftThread', action.payload.id);
    case 'COLLABORATORS.UPDATE_REJECTED':
      return moveRequestToRejected(state, 'UpdateCollaboratorsForDraftThread', action.payload.id, action.payload.error);
    case 'COLLABORATORS.UPDATE_PENDING':
      return moveRequestToPending(state, 'UpdateCollaboratorsForDraftThread', action.payload.id);

    case 'CREATE_TEAMS_MEETING.PENDING':
      return moveRequestToPending(state, 'CreateTeamsMeeting', action.payload.officeMeetingId);
    case 'CREATE_TEAMS_MEETING.FULFILLED':
      return moveRequestToFulfilled(state, 'CreateTeamsMeeting', action.payload.officeMeetingId);
    case 'CREATE_TEAMS_MEETING.REJECTED':
      return moveRequestToRejected(state, 'CreateTeamsMeeting', action.payload.officeMeetingId, action.payload.error);

    case 'DELETE_TEAMS_MEETING.PENDING':
      return moveRequestToPending(state, 'DeleteTeamsMeeting', action.payload.id);
    case 'DELETE_TEAMS_MEETING.FULFILLED':
      return moveRequestToFulfilled(state, 'DeleteTeamsMeeting', action.payload.id);
    case 'DELETE_TEAMS_MEETING.REJECTED':
      return moveRequestToRejected(state, 'DeleteTeamsMeeting', action.payload.id, action.payload.error);

    case 'TEAMS_MEETING.LOAD_PENDING':
      return moveRequestToPending(state, 'TeamsMeetingFeedLoad', action.payload);
    case 'TEAMS_MEETING.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'TeamsMeetingFeedLoad', action.payload.teamsMeetingId);
    case 'TEAMS_MEETING.LOAD_REJECTED':
      return moveRequestToRejected(state, 'TeamsMeetingFeedLoad', action.payload.teamsMeetingId, action.payload.error);

    case 'TEAMS_MEETINGS_BY_ORGANIZER.LOAD_PENDING':
      return moveRequestToPending(state, 'TeamsMeetingsByOrganizerLoad', action.payload.teamsMeetingsByOrganizersKey);
    case 'TEAMS_MEETINGS_BY_ORGANIZER.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'TeamsMeetingsByOrganizerLoad', action.payload.teamsMeetingsByOrganizersKey);

    case 'EDIT_TEAMS_MEETING.PENDING':
      return moveRequestToPending(state, 'EditTeamsMeeting', action.payload.teamsMeetingId);
    case 'EDIT_TEAMS_MEETING.FULFILLED':
      return moveRequestToFulfilled(state, 'EditTeamsMeeting', action.payload.teamsMeetingId);
    case 'EDIT_TEAMS_MEETING.REJECTED':
      return moveRequestToRejected(state, 'EditTeamsMeeting', action.payload.teamsMeetingId, action.payload.error);
    case 'EDIT_TENANT_CONFIGURATION_SETTINGS.PENDING':
      return moveRequestToPending(state, 'EditTenantConfigurationSettings', editTenantConfigurationSettingsRequestId);
    case 'EDIT_TENANT_CONFIGURATION_SETTINGS.FULFILLED':
      return moveRequestToFulfilled(state, 'EditTenantConfigurationSettings', editTenantConfigurationSettingsRequestId);
    case 'EDIT_TENANT_CONFIGURATION_SETTINGS.REJECTED':
      return moveRequestToRejected(
        state,
        'EditTenantConfigurationSettings',
        editTenantConfigurationSettingsRequestId,
        action.payload.error
      );
    case 'EDIT_USER_ACHIEVEMENT_ADMIN_SETTINGS.PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'EditUserAchievementAdminSettings',
        removeType: 'EditUserAchievementAdminSettings',
        entityId: action.editUserAchievementAdminSettingsRequestStatusKey,
      });
    case 'EDIT_USER_ACHIEVEMENT_ADMIN_SETTINGS.FULFILLED':
      return moveRequestToFulfilled(
        state,
        'EditUserAchievementAdminSettings',
        action.editUserAchievementAdminSettingsRequestStatusKey
      );
    case 'EDIT_USER_ACHIEVEMENT_ADMIN_SETTINGS.REJECTED':
      return moveRequestToRejected(
        state,
        'EditUserAchievementAdminSettings',
        action.editUserAchievementAdminSettingsRequestStatusKey,
        action.payload.error
      );
    case 'DISMISS_MODERATED_THREAD.PENDING':
      return moveRequestToPending(state, 'ModerateThread', action.payload.threadId);
    case 'DISMISS_MODERATED_THREAD.FULFILLED':
      return moveRequestToFulfilled(state, 'ModerateThread', action.payload.threadId);
    case 'DISMISS_MODERATED_THREAD.REJECTED':
      return moveRequestToRejected(state, 'ModerateThread', action.payload.threadId, action.payload.error);
    case 'DESIGNATE_LEADER.PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'DesignateLeader',
        removeType: 'RevokeLeader',
        entityId: action.payload.userId,
      });
    case 'DESIGNATE_LEADER.FULFILLED':
      return moveRequestToFulfilled(state, 'DesignateLeader', action.payload.userId);
    case 'DESIGNATE_LEADER.REJECTED':
      return moveRequestToRejected(state, 'DesignateLeader', action.payload.userId, action.payload.error);
    case 'EDIT_USER_ACHIEVEMENT_USER_SETTINGS.PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'EditUserAchievementUserSettings',
        removeType: 'EditUserAchievementUserSettings',
        entityId: action.payload.userId,
      });
    case 'EDIT_USER_ACHIEVEMENT_USER_SETTINGS.FULFILLED':
      return moveRequestToFulfilled(state, 'EditUserAchievementUserSettings', action.payload.userId);
    case 'EDIT_USER_ACHIEVEMENT_USER_SETTINGS.REJECTED':
      return moveRequestToRejected(
        state,
        'EditUserAchievementUserSettings',
        action.payload.userId,
        action.payload.error
      );
    case 'FEED.LOAD_NEWER_THREADS_PENDING':
      return moveRequestToPending(state, 'FeedLoadNewerThreads', action.payload.id);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return moveRequestToFulfilled(state, 'FeedLoadNewerThreads', action.payload.id);
    case 'FEED.LOAD_NEWER_THREADS_REJECTED':
      return moveRequestToRejected(state, 'FeedLoadNewerThreads', action.payload.id, action.payload.error);
    case 'FEED.LOAD_OLDER_THREADS_PENDING':
      return moveRequestToPending(state, 'FeedLoadOlderThreads', action.payload.id);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return moveRequestToFulfilled(state, 'FeedLoadOlderThreads', action.payload.id);
    case 'FEED.LOAD_OLDER_THREADS_REJECTED':
      return moveRequestToRejected(state, 'FeedLoadOlderThreads', action.payload.id, action.payload.error);

    case 'FILE.DELETE_PENDING':
      return moveRequestToPending(state, 'DeleteFile', action.payload.id);
    case 'FILE.DELETE_FULFILLED':
      return moveRequestToFulfilled(state, 'DeleteFile', action.payload.id);
    case 'FILE.IMAGE_DELETE_FULFILLED':
      return moveRequestToFulfilled(state, 'DeleteFile', action.payload.id);
    case 'FILE.VIDEO_DELETE_FULFILLED':
      return moveRequestToFulfilled(state, 'DeleteFile', action.payload.id);
    case 'FILE.DELETE_REJECTED':
      return moveRequestToRejected(state, 'DeleteFile', action.payload.id, action.payload.error);

    case 'GROUP.ADD_USER_PENDING':
      return moveRequestToPendingAndRemoveRelatedRequestTypes(
        state,
        'GroupAddUser',
        groupMemberPanelRelatedActions,
        action.payload.groupId
      );
    case 'GROUP.ADD_USER_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupAddUser', action.payload.groupId);
    case 'GROUP.ADD_USER_REJECTED':
      return moveRequestToRejected(
        state,
        'GroupAddUser',
        action.payload.groupId,
        getGraphqlApiErrorReason(action.payload.error)
      );

    case 'GROUP.ADD_USER_BY_EMAIL_PENDING':
      return moveRequestToPendingAndRemoveRelatedRequestTypes(
        state,
        'GroupAddUserByEmail',
        groupMemberPanelRelatedActions,
        action.payload.groupId
      );
    case 'GROUP.ADD_USER_BY_EMAIL_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupAddUserByEmail', action.payload.groupId);
    case 'GROUP.ADD_USER_BY_EMAIL_REJECTED':
      return moveRequestToRejected(
        state,
        'GroupAddUserByEmail',
        action.payload.groupId,
        getGraphqlApiErrorReason(action.payload.error)
      );

    case 'GROUP.REMOVE_USER_PENDING':
      return moveRequestToPendingAndRemoveRelatedRequestTypes(
        state,
        'GroupRemoveUser',
        groupMemberPanelRelatedActions,
        action.payload.groupId
      );
    case 'GROUP.REMOVE_USER_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupRemoveUser', action.payload.groupId);
    case 'GROUP.REMOVE_USER_REJECTED':
      return moveRequestToRejected(
        state,
        'GroupRemoveUser',
        action.payload.groupId,
        getGraphqlApiErrorReason(action.payload.error)
      );

    case 'GROUP.MAKE_ADMIN_PENDING':
      return moveRequestToPendingAndRemoveRelatedRequestTypes(
        state,
        'GroupMakeAdmin',
        groupMemberPanelRelatedActions,
        action.payload.groupId
      );
    case 'GROUP.MAKE_ADMIN_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupMakeAdmin', action.payload.groupId);
    case 'GROUP.MAKE_ADMIN_REJECTED':
      return moveRequestToRejected(
        state,
        'GroupMakeAdmin',
        action.payload.groupId,
        getGraphqlApiErrorReason(action.payload.error)
      );

    case 'GROUP.REVOKE_ADMIN_PENDING':
      return moveRequestToPendingAndRemoveRelatedRequestTypes(
        state,
        'GroupRevokeAdmin',
        groupMemberPanelRelatedActions,
        action.payload.groupId
      );
    case 'GROUP.REVOKE_ADMIN_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupRevokeAdmin', action.payload.groupId);
    case 'GROUP.REVOKE_ADMIN_REJECTED':
      return moveRequestToRejected(
        state,
        'GroupRevokeAdmin',
        action.payload.groupId,
        getGraphqlApiErrorReason(action.payload.error)
      );

    case 'GROUP_MEMBER_PANELS.DISMISS_ERRORS':
      return removeRelatedRequestTypes(state, groupMemberPanelRelatedActions, action.groupId);

    case 'GROUP_MEMBER_PANELS.LOAD_MORE_PENDING':
      return moveRequestToPending(state, 'GroupMemberPanelPaginations', action.payload.groupId);
    case 'GROUP_MEMBER_PANELS.LOAD_MORE_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupMemberPanelPaginations', action.payload.groupId);
    case 'GROUP_MEMBER_PANELS.LOAD_MORE_REJECTED':
      return moveRequestToRejected(state, 'GroupMemberPanelPaginations', action.payload.groupId, action.payload.error);

    case 'GROUP_COVER_IMAGE.DELETE_PENDING':
      return moveRequestToPending(state, 'CoverImageDelete', action.payload.groupId);
    case 'GROUP_COVER_IMAGE.DELETE_FULFILLED':
      return moveRequestToFulfilled(state, 'CoverImageDelete', action.payload.groupId);
    case 'GROUP_COVER_IMAGE.DELETE_REJECTED':
      return moveRequestToRejected(state, 'CoverImageDelete', action.payload.groupId, action.payload.error);

    case 'GROUP_COVER_IMAGE.UPLOAD_PENDING':
      return moveRequestToPending(state, 'CoverImageUpload', action.payload.groupId);
    case 'GROUP_COVER_IMAGE.UPLOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'CoverImageUpload', action.payload.groupId);
    case 'GROUP_COVER_IMAGE.UPLOAD_REJECTED':
      return moveRequestToRejected(state, 'CoverImageUpload', action.payload.groupId, action.payload.error);

    case 'GROUP.EDIT_PENDING':
      return moveRequestToPending(state, 'GroupEdit', action.payload.requestKey);
    case 'GROUP.EDIT_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupEdit', action.payload.requestKey);
    case 'GROUP.EDIT_REJECTED':
      return moveRequestToRejected(state, 'GroupEdit', action.payload.requestKey, action.payload.error);

    case 'GROUP.DELETE_PENDING':
      return moveRequestToPending(state, 'GroupDelete', action.payload.groupId);
    case 'GROUP.DELETE_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupDelete', action.payload.groupId);
    case 'GROUP.DELETE_REJECTED':
      return moveRequestToRejected(state, 'GroupDelete', action.payload.groupId, action.payload.error);

    case 'GROUP_EVENTS_SIDEBAR.LOAD_PENDING':
      return moveRequestToPending(state, 'GroupEventsSidebarLoad', action.payload.groupId);
    case 'GROUP_EVENTS_SIDEBAR.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupEventsSidebarLoad', action.payload.groupId);
    case 'GROUP_EVENTS_SIDEBAR.LOAD_REJECTED':
      return moveRequestToRejected(state, 'GroupEventsSidebarLoad', action.payload.groupId, action.payload.error);

    case 'GROUP_MEMBERS.BULK_UPLOAD_PENDING':
      return moveRequestToPendingAndRemoveRelatedRequestTypes(
        state,
        'GroupMembersBulkUpload',
        groupMemberPanelRelatedActions,
        action.payload.groupId
      );
    case 'GROUP_MEMBERS.BULK_UPLOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupMembersBulkUpload', action.payload.groupId);
    case 'GROUP_MEMBERS.BULK_UPLOAD_REJECTED':
      return moveRequestToRejected(state, 'GroupMembersBulkUpload', action.payload.groupId, action.payload.error);

    case 'GROUP.HIDE_FOR_NETWORK_IN_DISCOVERY_PENDING':
      return moveRequestToPending(state, 'GroupHideForNetworkInDiscovery', action.payload.groupId);
    case 'GROUP.HIDE_FOR_NETWORK_IN_DISCOVERY_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupHideForNetworkInDiscovery', action.payload.groupId);
    case 'GROUP.HIDE_FOR_NETWORK_IN_DISCOVERY_REJECTED':
      return moveRequestToRejected(
        state,
        'GroupHideForNetworkInDiscovery',
        action.payload.groupId,
        action.payload.error
      );

    case 'GROUP.UNHIDE_FOR_NETWORK_IN_DISCOVERY_PENDING':
      return moveRequestToPending(state, 'GroupUnhideForNetworkInDiscovery', action.payload.groupId);
    case 'GROUP.UNHIDE_FOR_NETWORK_IN_DISCOVERY_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupUnhideForNetworkInDiscovery', action.payload.groupId);
    case 'GROUP.UNHIDE_FOR_NETWORK_IN_DISCOVERY_REJECTED':
      return moveRequestToRejected(
        state,
        'GroupUnhideForNetworkInDiscovery',
        action.payload.groupId,
        action.payload.error
      );

    case 'GROUP.HIDE_FOR_VIEWER_IN_DISCOVERY_PENDING':
      return moveRequestToPending(state, 'GroupHideForViewerInDiscovery', action.payload.groupId);
    case 'GROUP.HIDE_FOR_VIEWER_IN_DISCOVERY_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupHideForViewerInDiscovery', action.payload.groupId);
    case 'GROUP.HIDE_FOR_VIEWER_IN_DISCOVERY_REJECTED':
      return moveRequestToRejected(
        state,
        'GroupHideForViewerInDiscovery',
        action.payload.groupId,
        action.payload.error
      );

    case 'GROUP.UNHIDE_FOR_VIEWER_IN_DISCOVERY_PENDING':
      return moveRequestToPending(state, 'GroupUnhideForViewerInDiscovery', action.payload.groupId);
    case 'GROUP.UNHIDE_FOR_VIEWER_IN_DISCOVERY_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupUnhideForViewerInDiscovery', action.payload.groupId);
    case 'GROUP.UNHIDE_FOR_VIEWER_IN_DISCOVERY_REJECTED':
      return moveRequestToRejected(
        state,
        'GroupUnhideForViewerInDiscovery',
        action.payload.groupId,
        action.payload.error
      );

    case 'GROUP_DISCOVERY_SETTINGS.LOAD_PENDING':
      return moveRequestToPending(state, 'GroupDiscoverySettingsLoad', action.payload.groupId);
    case 'GROUP_DISCOVERY_SETTINGS.LOAD_REJECTED':
      return moveRequestToRejected(state, 'GroupDiscoverySettingsLoad', action.payload.groupId, action.payload.error);
    case 'GROUP_DISCOVERY_SETTINGS.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupDiscoverySettingsLoad', action.payload.groupId);

    case 'GROUP_AVATAR_UPLOAD.PENDING':
      return removeRequest(state, 'CoverImageUpload', action.payload.groupId);

    case 'MESSAGE.DELETE_PENDING':
      return moveRequestToPending(state, 'DeleteMessage', action.payload.messageId);
    case 'MESSAGE.DELETE_FULFILLED':
      return moveRequestToFulfilled(state, 'DeleteMessage', action.payload.messageId);
    case 'MESSAGE.DELETE_REJECTED':
      return moveRequestToRejected(state, 'DeleteMessage', action.payload.messageId, action.payload.error);

    case 'MESSAGE.REPORT_PENDING':
      return moveRequestToPending(state, 'ReportMessage', action.payload.messageId);
    case 'MESSAGE.REPORT_FULFILLED':
      return moveRequestToFulfilled(state, 'ReportMessage', action.payload.messageId);
    case 'MESSAGE.REPORT_REJECTED':
      return moveRequestToRejected(state, 'ReportMessage', action.payload.messageId, action.payload.error);

    case 'MESSAGE_UPVOTES.LOAD_MORE_PENDING':
      return moveRequestToPending(state, 'MessageUpvotesLoadMore', action.payload.messageId);
    case 'MESSAGE_UPVOTES.LOAD_MORE_FULFILLED':
      return moveRequestToFulfilled(state, 'MessageUpvotesLoadMore', action.payload.messageId);
    case 'MESSAGE_UPVOTES.LOAD_MORE_REJECTED':
      return moveRequestToRejected(state, 'MessageUpvotesLoadMore', action.payload.messageId, action.payload.error);

    case 'NETWORK_QUESTION_SETTINGS.EDIT_FULFILLED':
      return moveRequestToFulfilled(state, 'EditNetworkQuestionSettings', editNetworkQuestionsSettingsRequestId);
    case 'OFFICE_TOPIC.LOAD_PENDING':
      return moveRequestToPending(state, 'OfficeTopicLoadAuthorized', action.payload.publisherId);
    case 'OFFICE_TOPIC.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'OfficeTopicLoadAuthorized', action.payload.publisherId);
    case 'OFFICE_TOPIC.LOAD_REJECTED':
      return moveRequestToRejected(
        state,
        'OfficeTopicLoadAuthorized',
        action.payload.publisherId,
        action.payload.error
      );
    case 'POLL.CLOSE_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'PollClose',
        removeType: 'PollReopen',
        entityId: action.payload.messageId,
      });
    case 'POLL.CLOSE_FULFILLED':
      return moveRequestToFulfilled(state, 'PollClose', action.payload.messageId);
    case 'POLL.CLOSE_REJECTED':
      return moveRequestToRejected(state, 'PollClose', action.payload.messageId, action.payload.error);
    case 'POLL.REOPEN_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'PollReopen',
        removeType: 'PollClose',
        entityId: action.payload.messageId,
      });
    case 'POLL.REOPEN_FULFILLED':
      return moveRequestToFulfilled(state, 'PollReopen', action.payload.messageId);
    case 'POLL.REOPEN_REJECTED':
      return moveRequestToRejected(state, 'PollReopen', action.payload.messageId, action.payload.error);
    case 'POLL.LOAD_PENDING':
      return moveRequestToPending(state, 'PollLoad', action.payload.id);
    case 'POLL.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'PollLoad', action.payload.id);
    case 'POLL.LOAD_REJECTED':
      return moveRequestToRejected(state, 'PollLoad', action.payload.id, action.payload.error);
    case 'POLL.VOTE_PENDING':
      return moveRequestToPending(state, 'PollVote', action.payload.id);
    case 'POLL.VOTE_REJECTED':
      return moveRequestToRejected(state, 'PollVote', action.payload.id, action.payload.error);
    case 'PUBLISH_MODERATED_THREAD.PENDING':
      return moveRequestToPending(state, 'ModerateThread', action.payload.threadId);
    case 'PUBLISH_MODERATED_THREAD.FULFILLED':
      return moveRequestToFulfilled(state, 'ModerateThread', action.payload.threadId);
    case 'PUBLISH_MODERATED_THREAD.REJECTED':
      return moveRequestToRejected(state, 'ModerateThread', action.payload.threadId, action.payload.error);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_PENDING':
      return moveRequestToPending(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_PENDING':
      return moveRequestToPending(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_PENDING':
      return moveRequestToPending(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_PENDING':
      return moveRequestToPending(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_PENDING':
      return moveRequestToPending(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_PENDING':
      return moveRequestToPending(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_PENDING':
      return moveRequestToPending(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_PENDING':
      return moveRequestToPending(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_PENDING':
      return moveRequestToPending(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_PENDING':
      return moveRequestToPending(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_NETWORK_QUESTION_MESSAGE_PENDING':
      return moveRequestToPending(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.NON_REDUX_PUBLISH_PENDING':
      return moveRequestToPending(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_PENDING':
      return moveRequestToPending(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_TEAMS_MEETING_MESSAGE_PENDING':
      return moveRequestToPending(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_TEAMS_MEETING_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_NETWORK_QUESTION_MESSAGE_FULFILLED':
      return moveRequestToFulfilled(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.NON_REDUX_PUBLISH_FULFILLED':
      return moveRequestToFulfilled(state, 'PublishMessage', action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        'PublishMessage',
        action.payload.publisherId,
        getGraphqlApiErrorReason(action.payload.error)
      );
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        'PublishMessage',
        action.payload.publisherId,
        getGraphqlApiErrorCode(action.payload)
      );
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        'PublishMessage',
        action.payload.publisherId,
        getGraphqlApiErrorCode(action.payload)
      );
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        'PublishMessage',
        action.payload.publisherId,
        getGraphqlApiErrorCode(action.payload)
      );
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        'PublishMessage',
        action.payload.publisherId,
        getGraphqlApiErrorCode(action.payload)
      );
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        'PublishMessage',
        action.payload.publisherId,
        getGraphqlApiErrorCode(action.payload)
      );
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        'PublishMessage',
        action.payload.publisherId,
        getGraphqlApiErrorCode(action.payload)
      );
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        'PublishMessage',
        action.payload.publisherId,
        getGraphqlApiErrorReason(action.payload.error)
      );
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        'PublishMessage',
        action.payload.publisherId,
        getGraphqlApiErrorReason(action.payload.error)
      );
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        'PublishMessage',
        action.payload.publisherId,
        getGraphqlApiErrorCode(action.payload)
      );
    case 'PUBLISHER.PUBLISH_TEAMS_MEETING_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        'PublishMessage',
        action.payload.publisherId,
        getGraphqlApiErrorReason(action.payload.error)
      );
    case 'PUBLISHER.PUBLISH_NETWORK_QUESTION_MESSAGE_REJECTED':
      return moveRequestToRejected(
        state,
        'PublishMessage',
        action.payload.publisherId,
        getGraphqlApiErrorReason(action.payload.error)
      );
    case 'PUBLISHER.NON_REDUX_PUBLISH_REJECTED':
      return moveRequestToRejected(
        state,
        'PublishMessage',
        action.payload.publisherId,
        getGraphqlApiErrorReason(action.payload.error)
      );
    case 'PUBLISHER.REMOVE_PUBLISH_MESSAGE_REQUEST':
      return removeRequest(state, 'PublishMessage', action.payload.id);

    case 'FEED.MARK_ALL_GROUP_MESSAGES_AS_SEEN_PENDING':
      return moveRequestToPending(state, 'MarkAllGroupThreadsAsSeen', action.payload.feedId);
    case 'FEED.MARK_ALL_GROUP_MESSAGES_AS_SEEN_FULFILLED':
      return moveRequestToFulfilled(state, 'MarkAllGroupThreadsAsSeen', action.payload.feedId);
    case 'FEED.MARK_ALL_GROUP_MESSAGES_AS_SEEN_REJECTED':
      return moveRequestToRejected(state, 'MarkAllGroupThreadsAsSeen', action.payload.feedId, action.payload.error);

    case 'REMOVE_FROM_DIRECT_AUDIENCE.PENDING':
      return moveRequestToPending(state, 'RemoveFromDirectAudience', action.payload.userId);
    case 'REMOVE_FROM_DIRECT_AUDIENCE.FULFILLED':
      return stateWithRemovedFromDirectAudience(state, action);
    case 'REMOVE_FROM_DIRECT_AUDIENCE.REJECTED':
      return moveRequestToRejected(state, 'RemoveFromDirectAudience', action.payload.userId, action.payload.error);
    case 'RESTORE_MODERATED_THREAD.PENDING':
      return moveRequestToPending(state, 'ModerateThread', action.payload.threadId);
    case 'RESTORE_MODERATED_THREAD.FULFILLED':
      return moveRequestToFulfilled(state, 'ModerateThread', action.payload.threadId);
    case 'RESTORE_MODERATED_THREAD.REJECTED':
      return moveRequestToRejected(state, 'ModerateThread', action.payload.threadId, action.payload.error);
    case 'REVOKE_LEADER.PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'RevokeLeader',
        removeType: 'DesignateLeader',
        entityId: action.payload.userId,
      });
    case 'REVOKE_LEADER.FULFILLED':
      return moveRequestToFulfilled(state, 'RevokeLeader', action.payload.userId);
    case 'REVOKE_LEADER.REJECTED':
      return moveRequestToRejected(state, 'RevokeLeader', action.payload.userId, action.payload.error);
    case 'THREAD.DELETE_PENDING':
      return moveRequestToPending(state, 'DeleteThread', action.payload.threadId);
    case 'THREAD.DELETE_FULFILLED':
      return moveRequestToFulfilled(state, 'DeleteThread', action.payload.threadId);
    case 'THREAD.DELETE_REJECTED':
      return moveRequestToRejected(state, 'DeleteThread', action.payload.threadId, action.payload.error);
    case 'THREADS.DELETE_PENDING':
      return moveRequestToPending(state, 'DeleteDraftThreads', deleteDraftThreadsRequestStatusId);
    case 'THREADS.DELETE_FULFILLED':
      return moveRequestToFulfilled(state, 'DeleteDraftThreads', deleteDraftThreadsRequestStatusId);
    case 'THREADS.DELETE_REJECTED':
      return moveRequestToRejected(
        state,
        'DeleteDraftThreads',
        deleteDraftThreadsRequestStatusId,
        action.payload.error
      );
    case 'THREAD.PIN_TO_CAMPAIGN_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'ThreadPinToCampaignFeed',
        removeType: 'ThreadUnpinFromCampaignFeed',
        entityId: action.payload.threadId,
      });
    case 'THREAD.PIN_TO_CAMPAIGN_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadPinToCampaignFeed', action.payload.threadId);
    case 'THREAD.PIN_TO_CAMPAIGN_REJECTED':
      return moveRequestToRejected(state, 'ThreadPinToCampaignFeed', action.payload.threadId, action.payload.error);
    case 'THREAD.HIDE_FROM_CAMPAIGN_PENDING':
      return moveRequestToPending(state, 'ThreadHideFromCampaignFeed', action.payload.threadId);
    case 'THREAD.HIDE_FROM_CAMPAIGN_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadHideFromCampaignFeed', action.payload.threadId);
    case 'THREAD.HIDE_FROM_CAMPAIGN_REJECTED':
      return moveRequestToRejected(state, 'ThreadHideFromCampaignFeed', action.payload.threadId, action.payload.error);
    case 'THREAD.ARCHIVE_FROM_TEAMS_MEETING_PENDING':
      return moveRequestToPending(state, 'ThreadArchiveFromTeamsMeetingFeed', action.payload.threadId);
    case 'THREAD.ARCHIVE_FROM_TEAMS_MEETING_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadArchiveFromTeamsMeetingFeed', action.payload.threadId);
    case 'THREAD.ARCHIVE_FROM_TEAMS_MEETING_REJECTED':
      return moveRequestToRejected(
        state,
        'ThreadArchiveFromTeamsMeetingFeed',
        action.payload.threadId,
        action.payload.error
      );
    case 'THREAD.UNARCHIVE_FROM_TEAMS_MEETING_PENDING':
      return moveRequestToPending(state, 'ThreadUnarchiveFromTeamsMeetingFeed', action.payload.threadId);
    case 'THREAD.UNARCHIVE_FROM_TEAMS_MEETING_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadUnarchiveFromTeamsMeetingFeed', action.payload.threadId);
    case 'THREAD.UNARCHIVE_FROM_TEAMS_MEETING_REJECTED':
      return moveRequestToRejected(
        state,
        'ThreadUnarchiveFromTeamsMeetingFeed',
        action.payload.threadId,
        action.payload.error
      );
    case 'FEED.ARCHIVE_TEAMS_MEETING_PENDING':
      return moveRequestToPending(state, 'ArchiveTeamsMeetingFeed', action.payload.teamsMeetingId);
    case 'FEED.ARCHIVE_TEAMS_MEETING_FULFILLED':
      return moveRequestToFulfilled(state, 'ArchiveTeamsMeetingFeed', action.payload.teamsMeetingId);
    case 'FEED.ARCHIVE_TEAMS_MEETING_REJECTED':
      return moveRequestToRejected(
        state,
        'ArchiveTeamsMeetingFeed',
        action.payload.teamsMeetingId,
        action.payload.error
      );
    case 'THREAD.PIN_TO_EVENT_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'ThreadPinToEventFeed',
        removeType: 'ThreadUnpinFromEventFeed',
        entityId: action.payload.threadId,
      });
    case 'THREAD.PIN_TO_EVENT_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadPinToEventFeed', action.payload.threadId);
    case 'THREAD.PIN_TO_EVENT_REJECTED':
      return moveRequestToRejected(state, 'ThreadPinToEventFeed', action.payload.threadId, action.payload.error);

    case 'THREAD.PIN_TO_USER_STORYLINE_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'ThreadPinToUserStorylineFeed',
        removeType: 'ThreadUnpinFromUserStorylineFeed',
        entityId: action.payload.threadId,
      });
    case 'THREAD.PIN_TO_USER_STORYLINE_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadPinToUserStorylineFeed', action.payload.threadId);
    case 'THREAD.PIN_TO_USER_STORYLINE_REJECTED':
      return moveRequestToRejected(
        state,
        'ThreadPinToUserStorylineFeed',
        action.payload.threadId,
        action.payload.error
      );

    case 'THREAD.PIN_TO_GROUP_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'ThreadPinToGroupFeed',
        removeType: 'ThreadUnpinFromGroupFeed',
        entityId: action.payload.threadId,
      });
    case 'THREAD.PIN_TO_GROUP_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadPinToGroupFeed', action.payload.threadId);
    case 'THREAD.PIN_TO_GROUP_REJECTED':
      return moveRequestToRejected(state, 'ThreadPinToGroupFeed', action.payload.threadId, action.payload.error);

    case 'THREAD.PIN_TO_MY_LEADERS_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'ThreadPinToMyLeadersFeed',
        removeType: 'ThreadUnpinFromMyLeadersFeed',
        entityId: action.payload.threadId,
      });
    case 'THREAD.PIN_TO_MY_LEADERS_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadPinToMyLeadersFeed', action.payload.threadId);
    case 'THREAD.PIN_TO_MY_LEADERS_REJECTED':
      return moveRequestToRejected(state, 'ThreadPinToMyLeadersFeed', action.payload.threadId, action.payload.error);

    case 'THREAD.PIN_TO_TEAMS_MEETING_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'ThreadPinToTeamsMeetingFeed',
        removeType: 'ThreadUnpinFromTeamsMeetingFeed',
        entityId: action.payload.threadId,
      });
    case 'THREAD.PIN_TO_TEAMS_MEETING_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadPinToTeamsMeetingFeed', action.payload.threadId);
    case 'THREAD.PIN_TO_TEAMS_MEETING_REJECTED':
      return moveRequestToRejected(state, 'ThreadPinToTeamsMeetingFeed', action.payload.threadId, action.payload.error);

    case 'THREAD.UNPIN_FROM_CAMPAIGN_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'ThreadUnpinFromCampaignFeed',
        removeType: 'ThreadPinToCampaignFeed',
        entityId: action.payload.threadId,
      });
    case 'THREAD.UNPIN_FROM_CAMPAIGN_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadUnpinFromCampaignFeed', action.payload.threadId);
    case 'THREAD.UNPIN_FROM_CAMPAIGN_REJECTED':
      return moveRequestToRejected(state, 'ThreadUnpinFromCampaignFeed', action.payload.threadId, action.payload.error);

    case 'THREAD.UNPIN_FROM_EVENT_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'ThreadUnpinFromEventFeed',
        removeType: 'ThreadPinToEventFeed',
        entityId: action.payload.threadId,
      });
    case 'THREAD.UNPIN_FROM_EVENT_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadUnpinFromEventFeed', action.payload.threadId);
    case 'THREAD.UNPIN_FROM_EVENT_REJECTED':
      return moveRequestToRejected(state, 'ThreadUnpinFromEventFeed', action.payload.threadId, action.payload.error);

    case 'THREAD.UNPIN_FROM_USER_STORYLINE_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'ThreadUnpinFromUserStorylineFeed',
        removeType: 'ThreadUnpinFromUserStorylineFeed',
        entityId: action.payload.threadId,
      });
    case 'THREAD.UNPIN_FROM_USER_STORYLINE_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadUnpinFromUserStorylineFeed', action.payload.threadId);
    case 'THREAD.UNPIN_FROM_USER_STORYLINE_REJECTED':
      return moveRequestToRejected(
        state,
        'ThreadUnpinFromUserStorylineFeed',
        action.payload.threadId,
        action.payload.error
      );

    case 'THREAD.UNPIN_FROM_GROUP_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'ThreadUnpinFromGroupFeed',
        removeType: 'ThreadPinToGroupFeed',
        entityId: action.payload.threadId,
      });
    case 'THREAD.UNPIN_FROM_GROUP_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadUnpinFromGroupFeed', action.payload.threadId);
    case 'THREAD.UNPIN_FROM_GROUP_REJECTED':
      return moveRequestToRejected(state, 'ThreadUnpinFromGroupFeed', action.payload.threadId, action.payload.error);

    case 'THREAD.UNPIN_FROM_MY_LEADERS_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'ThreadUnpinFromMyLeadersFeed',
        removeType: 'ThreadPinToMyLeadersFeed',
        entityId: action.payload.threadId,
      });
    case 'THREAD.UNPIN_FROM_MY_LEADERS_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadUnpinFromMyLeadersFeed', action.payload.threadId);
    case 'THREAD.UNPIN_FROM_MY_LEADERS_REJECTED':
      return moveRequestToRejected(
        state,
        'ThreadUnpinFromMyLeadersFeed',
        action.payload.threadId,
        action.payload.error
      );

    case 'THREAD.UNPIN_FROM_TEAMS_MEETING_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'ThreadUnpinFromTeamsMeetingFeed',
        removeType: 'ThreadPinToTeamsMeetingFeed',
        entityId: action.payload.threadId,
      });
    case 'THREAD.UNPIN_FROM_TEAMS_MEETING_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadUnpinFromTeamsMeetingFeed', action.payload.threadId);
    case 'THREAD.UNPIN_FROM_TEAMS_MEETING_REJECTED':
      return moveRequestToRejected(
        state,
        'ThreadUnpinFromTeamsMeetingFeed',
        action.payload.threadId,
        action.payload.error
      );

    case 'THREAD.CLOSE_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'ThreadClose',
        removeType: 'ThreadReopen',
        entityId: action.payload.id,
      });
    case 'THREAD.CLOSE_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadClose', action.payload.id);
    case 'THREAD.CLOSE_REJECTED':
      return moveRequestToRejected(state, 'ThreadClose', action.payload.id, action.payload.error);

    case 'THREAD.REOPEN_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'ThreadReopen',
        removeType: 'ThreadClose',
        entityId: action.payload.id,
      });
    case 'THREAD.REOPEN_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadReopen', action.payload.id);
    case 'THREAD.REOPEN_REJECTED':
      return moveRequestToRejected(state, 'ThreadReopen', action.payload.id, action.payload.error);

    case 'THREAD.BOOKMARK_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadBookmark', action.payload.threadId);
    case 'THREAD.BOOKMARK_PENDING':
      return moveRequestToPending(state, 'ThreadBookmark', action.payload.threadId);
    case 'THREAD.BOOKMARK_REJECTED':
      return moveRequestToRejected(state, 'ThreadBookmark', action.payload.threadId, action.payload.error);

    case 'THREAD.UNBOOKMARK_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadUnbookmark', action.payload.threadId);
    case 'THREAD.UNBOOKMARK_PENDING':
      return moveRequestToPending(state, 'ThreadUnbookmark', action.payload.threadId);
    case 'THREAD.UNBOOKMARK_REJECTED':
      return moveRequestToRejected(state, 'ThreadUnbookmark', action.payload.threadId, action.payload.error);

    case 'THREAD.MOVE_PENDING':
      return moveRequestToPending(state, 'ThreadMove', action.payload.threadId);
    case 'THREAD.MOVE_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadMove', action.payload.threadId);
    case 'THREAD.MOVE_REJECTED':
      return moveRequestToRejected(state, 'ThreadMove', action.payload.threadId, action.payload.error);

    case 'THREAD.MUTE_PENDING':
      return moveRequestToPending(state, 'ThreadMuteConversation', action.payload.threadId);
    case 'THREAD.MUTE_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadMuteConversation', action.payload.threadId);
    case 'THREAD.MUTE_REJECTED':
      return moveRequestToRejected(state, 'ThreadMuteConversation', action.payload.threadId, action.payload.error);

    case 'THREAD.UNMUTE_PENDING':
      return moveRequestToPending(state, 'ThreadUnmuteConversation', action.payload.threadId);
    case 'THREAD.UNMUTE_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadUnmuteConversation', action.payload.threadId);
    case 'THREAD.UNMUTE_REJECTED':
      return moveRequestToRejected(state, 'ThreadUnmuteConversation', action.payload.threadId, action.payload.error);

    case 'THREAD.REMOVE_PARTICIPANTS_PENDING':
      return moveRequestToPending(state, 'ThreadRemoveParticipants', action.payload.messageId);
    case 'THREAD.REMOVE_PARTICIPANTS_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadRemoveParticipants', action.payload.messageId);
    case 'THREAD.REMOVE_PARTICIPANTS_REJECTED':
      return moveRequestToRejected(state, 'ThreadRemoveParticipants', action.payload.messageId, action.payload.error);

    case 'THREAD.PROMOTE_PENDING':
      return moveRequestToPending(state, 'PromoteThread', action.payload.threadId);
    case 'THREAD.PROMOTE_FULFILLED':
      return moveRequestToFulfilled(state, 'PromoteThread', action.payload.threadId);
    case 'THREAD.PROMOTE_REJECTED':
      return moveRequestToRejected(state, 'PromoteThread', action.payload.threadId, action.payload.error);
    case 'THREAD.UNPROMOTE_PENDING':
      return moveRequestToPending(state, 'UnpromoteThread', action.payload.threadId);
    case 'THREAD.UNPROMOTE_FULFILLED':
      return moveRequestToFulfilled(state, 'UnpromoteThread', action.payload.threadId);
    case 'THREAD.UNPROMOTE_REJECTED':
      return moveRequestToRejected(state, 'UnpromoteThread', action.payload.threadId, action.payload.error);

    case 'UNSEEN_THREADS.LOAD_PENDING':
      return moveRequestToPending(state, 'UnseenThreadsLoad', action.payload.id);
    case 'UNSEEN_THREADS.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'UnseenThreadsLoad', action.payload.id);
    case 'UNSEEN_THREADS.LOAD_REJECTED':
      return moveRequestToRejected(state, 'UnseenThreadsLoad', action.payload.id, action.payload.error);

    case 'USER_COVER_IMAGE.DELETE_PENDING':
      return moveRequestToPending(state, 'CoverImageDelete', action.payload.userId);
    case 'USER_COVER_IMAGE.DELETE_FULFILLED':
      return moveRequestToFulfilled(state, 'CoverImageDelete', action.payload.userId);
    case 'USER_COVER_IMAGE.DELETE_REJECTED':
      return moveRequestToRejected(state, 'CoverImageDelete', action.payload.userId, action.payload.error);

    case 'USER_COVER_IMAGE.UPLOAD_PENDING':
      return moveRequestToPending(state, 'CoverImageUpload', action.payload.userId);
    case 'USER_COVER_IMAGE.UPLOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'CoverImageUpload', action.payload.userId);
    case 'USER_COVER_IMAGE.UPLOAD_REJECTED':
      return moveRequestToRejected(state, 'CoverImageUpload', action.payload.userId, action.payload.error);

    case 'CAMPAIGN_COVER_IMAGE.DELETE_PENDING':
      return moveRequestToPending(state, 'CoverImageDelete', action.payload.campaignId);
    case 'CAMPAIGN_COVER_IMAGE.DELETE_FULFILLED':
      return moveRequestToFulfilled(state, 'CoverImageDelete', action.payload.campaignId);
    case 'CAMPAIGN_COVER_IMAGE.DELETE_REJECTED':
      return moveRequestToRejected(state, 'CoverImageDelete', action.payload.campaignId, action.payload.error);

    case 'CAMPAIGN_COVER_IMAGE.UPLOAD_PENDING':
      return moveRequestToPending(state, 'CoverImageUpload', action.payload.campaignId);
    case 'CAMPAIGN_COVER_IMAGE.UPLOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'CoverImageUpload', action.payload.campaignId);
    case 'CAMPAIGN_COVER_IMAGE.UPLOAD_REJECTED':
      return moveRequestToRejected(state, 'CoverImageUpload', action.payload.campaignId, action.payload.error);

    case 'FEED_COUNTS.LOAD_GROUP_PENDING':
      return moveRequestToPending(state, 'GroupFeedUnreadCount', action.payload.id);
    case 'FEED_COUNTS.LOAD_GROUP_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupFeedUnreadCount', action.payload.id);
    case 'FEED_COUNTS.LOAD_GROUP_REJECTED':
      return moveRequestToRejected(state, 'GroupFeedUnreadCount', action.payload.id, action.payload.error);

    case 'GROUP.RESOLVE_MEMBERSHIP_REQUEST_PENDING':
      return moveRequestToPendingAndRemoveRelatedRequestTypes(
        state,
        'GroupResolveMembership',
        groupMemberPanelRelatedActions,
        action.payload.groupMembershipRequestId
      );
    case 'GROUP.RESOLVE_MEMBERSHIP_REQUEST_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupResolveMembership', action.payload.groupMembershipRequestId);
    case 'GROUP.RESOLVE_MEMBERSHIP_REQUEST_REJECTED':
      return moveRequestToRejected(
        state,
        'GroupResolveMembership',
        action.payload.groupMembershipRequestId,
        action.payload.error
      );

    case 'CANCEL_EVENT.PENDING':
      return moveRequestToPending(state, 'CancelEvent', action.payload);
    case 'CANCEL_EVENT.FULFILLED':
      return stateWithCancelledEvent(state, action);
    case 'CANCEL_EVENT.REJECTED':
      return moveRequestToRejected(state, 'CancelEvent', action.payload.broadcastId, action.payload.error);
    case 'CANCEL_GENERATE_CURATED_THREADS.PENDING':
      return moveRequestToPending(state, 'CancelGenerateCuratedThreads', action.payload.curatedThreadListId);
    case 'CANCEL_GENERATE_CURATED_THREADS.FULFILLED':
      return moveRequestToFulfilled(state, 'CancelGenerateCuratedThreads', action.payload.curatedThreadListId);
    case 'CANCEL_GENERATE_CURATED_THREADS.REJECTED':
      return moveRequestToRejected(
        state,
        'CancelGenerateCuratedThreads',
        action.payload.curatedThreadListId,
        action.payload.error
      );
    case 'LIVE_EVENT.LOAD_BY_NETWORK_PENDING':
      return moveRequestToPending(state, 'LiveEventsLoadByNetwork', liveEventsLoadByNetworkRequestId);
    case 'LIVE_EVENT.LOAD_BY_NETWORK_FULFILLED':
      return moveRequestToFulfilled(state, 'LiveEventsLoadByNetwork', liveEventsLoadByNetworkRequestId);
    case 'LIVE_EVENT.LOAD_BY_NETWORK_REJECTED':
      return moveRequestToRejected(
        state,
        'LiveEventsLoadByNetwork',
        liveEventsLoadByNetworkRequestId,
        action.payload.error
      );

    case 'EDIT_EVENT.PENDING':
      return moveRequestToPending(state, 'EditEvent', action.payload.broadcastId);
    case 'EDIT_EVENT.FULFILLED':
      return moveRequestToFulfilled(state, 'EditEvent', action.payload.broadcastId);
    case 'EDIT_EVENT.REJECTED':
      return moveRequestToRejected(state, 'EditEvent', action.payload.broadcastId, action.payload.error);

    case 'EDIT_RESTRICTED_POSTING.PENDING':
      return moveRequestToPending(state, 'EditRestrictedPosting', action.payload.broadcastId);
    case 'EDIT_RESTRICTED_POSTING.FULFILLED':
      return moveRequestToFulfilled(state, 'EditRestrictedPosting', action.payload.broadcastId);
    case 'EDIT_RESTRICTED_POSTING.REJECTED':
      return moveRequestToRejected(state, 'EditRestrictedPosting', action.payload.broadcastId, action.payload.error);

    case 'CREATE_GROUP_CALENDAR_INVITE_FOR_BROADCAST.PENDING':
      return moveRequestToPending(state, 'CreateGroupCalendarInviteForBroadcast', action.payload.broadcastId);
    case 'CREATE_GROUP_CALENDAR_INVITE_FOR_BROADCAST.FULFILLED':
      return moveRequestToFulfilled(state, 'CreateGroupCalendarInviteForBroadcast', action.payload.broadcastId);
    case 'CREATE_GROUP_CALENDAR_INVITE_FOR_BROADCAST.REJECTED':
      return moveRequestToRejected(
        state,
        'CreateGroupCalendarInviteForBroadcast',
        action.payload.broadcastId,
        action.payload.error
      );

    case 'EVENT.LOAD_BY_BROADCAST_ID_PENDING':
      return moveRequestToPending(state, 'EventLoadByBroadcastId', action.payload);
    case 'EVENT.LOAD_BY_BROADCAST_ID_FULFILLED':
      return moveRequestToFulfilled(state, 'EventLoadByBroadcastId', action.payload.broadcastId);
    case 'EVENT.LOAD_BY_BROADCAST_ID_REJECTED':
      return moveRequestToRejected(state, 'EventLoadByBroadcastId', action.payload.broadcastId, action.payload.error);

    case 'COMMUNITY.ADD_FAVORITE_REJECTED':
      return moveRequestToRejected(state, 'AddFavoriteGroup', addFavoriteGroupRequestId, action.payload.error);

    case 'THREAD_TOPIC.ADD_PENDING':
      return moveRequestToPending(state, 'AddThreadTopic', action.payload.threadId);
    case 'THREAD_TOPIC.ADD_FULFILLED':
      return moveRequestToFulfilled(state, 'AddThreadTopic', action.payload.threadId);
    case 'THREAD_TOPIC.ADD_REJECTED':
      return moveRequestToRejected(state, 'AddThreadTopic', action.payload.threadId, action.payload.error);

    case 'THREAD_TOPIC.REMOVE_PENDING':
      return moveRequestToPending(state, 'RemoveThreadTopic', action.payload.threadId);
    case 'THREAD_TOPIC.REMOVE_FULFILLED':
      return moveRequestToFulfilled(state, 'RemoveThreadTopic', action.payload.threadId);
    case 'THREAD_TOPIC.REMOVE_REJECTED':
      return moveRequestToRejected(state, 'RemoveThreadTopic', action.payload.threadId, action.payload.error);

    case 'THREAD.UNMARK_AS_QUESTION_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'ThreadUnmarkAsQuestion',
        removeType: 'ThreadMarkAsQuestion',
        entityId: action.payload.messageId,
      });
    case 'THREAD.UNMARK_AS_QUESTION_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadUnmarkAsQuestion', action.payload.messageId);
    case 'THREAD.UNMARK_AS_QUESTION_REJECTED':
      return moveRequestToRejected(state, 'ThreadUnmarkAsQuestion', action.payload.messageId, action.payload.error);

    case 'THREAD.MARK_AS_QUESTION_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'ThreadMarkAsQuestion',
        removeType: 'ThreadUnmarkAsQuestion',
        entityId: action.payload.messageId,
      });
    case 'THREAD.MARK_AS_QUESTION_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadMarkAsQuestion', action.payload.messageId);
    case 'THREAD.MARK_AS_QUESTION_REJECTED':
      return moveRequestToRejected(state, 'ThreadMarkAsQuestion', action.payload.messageId, action.payload.error);

    case 'USER_DELEGATE.REMOVE_PENDING':
      return moveRequestToPending(state, 'DelegationRemove', removeUserDelegateRequestId);
    case 'USER_DELEGATE.REMOVE_FULFILLED':
      return moveRequestToFulfilled(state, 'DelegationRemove', removeUserDelegateRequestId);
    case 'USER_DELEGATE.REMOVE_REJECTED':
      return moveRequestToRejected(state, 'DelegationRemove', removeUserDelegateRequestId, action.payload.error);

    case 'USER_DELEGATE.ADD_REJECTED':
      return moveRequestToRejected(state, 'DelegationAdd', addUserDelegateRequestId, action.payload.error);
    case 'USER_DELEGATE.ADD_FULFILLED':
      return moveRequestToFulfilled(state, 'DelegationAdd', addUserDelegateRequestId);
    case 'USER_DELEGATE.EDIT_PENDING':
      return moveRequestToPending(state, 'DelegationEdit', editUserDelegateRequestId);
    case 'USER_DELEGATE.EDIT_FULFILLED':
      return moveRequestToFulfilled(state, 'DelegationEdit', editUserDelegateRequestId);
    case 'USER_DELEGATE.EDIT_REJECTED':
      return moveRequestToRejected(state, 'DelegationEdit', editUserDelegateRequestId, action.payload.error);

    case 'SEARCH.LOAD_MORE_PENDING':
      return moveRequestToPending(state, 'SearchLoadMore', action.payload.loadMoreRequestId);
    case 'SEARCH.LOAD_MORE_FULFILLED':
      return moveRequestToFulfilled(state, 'SearchLoadMore', action.payload.loadMoreRequestId);
    case 'SEARCH.LOAD_MORE_REJECTED':
      return moveRequestToRejected(state, 'SearchLoadMore', action.payload.loadMoreRequestId, action.payload.error);

    case 'GROUP_PINNED_RESOURCES.EDIT_LINK_TITLE_PENDING':
      return moveRequestToPending(state, 'PinnedResourcesPinLink', action.payload.groupId);
    case 'GROUP_PINNED_RESOURCES.EDIT_LINK_TITLE_REJECTED':
      return moveRequestToRejected(state, 'PinnedResourcesPinLink', action.payload.groupId, action.payload.error);
    case 'GROUP_PINNED_RESOURCES.EDIT_LINK_TITLE_FULFILLED':
      return removeRequest(state, 'PinnedResourcesPinLink', action.payload.groupId);
    case 'CAMPAIGN_PINNED_RESOURCES.PIN_OBJECT_PENDING':
      return moveRequestToPending(state, 'CampaignPinnedResourcesPinLink', action.payload.campaignId);
    case 'CAMPAIGN_PINNED_RESOURCES.PIN_OBJECT_REJECTED':
      return moveRequestToRejected(
        state,
        'CampaignPinnedResourcesPinLink',
        action.payload.campaignId,
        action.payload.error
      );
    case 'CAMPAIGN_PINNED_RESOURCES.PIN_OBJECT_FULFILLED':
      return moveRequestToFulfilled(state, 'CampaignPinnedResourcesPinLink', action.payload.campaignId);

    case 'CAMPAIGN.EDIT_PENDING':
      return moveRequestToPending(state, 'CampaignEdit', action.payload.campaignId);
    case 'CAMPAIGN.EDIT_REJECTED':
      return moveRequestToRejected(state, 'CampaignEdit', action.payload.campaignId, action.payload.error);
    case 'CAMPAIGN.EDIT_FULFILLED':
      return moveRequestToFulfilled(state, 'CampaignEdit', action.payload.id);

    case 'CAMPAIGN.PUBLISH_PENDING':
      return moveRequestToPending(state, 'CampaignPublish', action.payload.campaignId);
    case 'CAMPAIGN.PUBLISH_REJECTED':
      return moveRequestToRejected(state, 'CampaignPublish', action.payload.campaignId, action.payload.error);
    case 'CAMPAIGN.PUBLISH_FULFILLED':
      return moveRequestToFulfilled(state, 'CampaignPublish', action.payload.id);

    case 'CAMPAIGN.DELETE_PENDING':
      return moveRequestToPending(state, 'CampaignDelete', action.payload.campaignId);
    case 'CAMPAIGN.DELETE_REJECTED':
      return moveRequestToRejected(state, 'CampaignDelete', action.payload.campaignId, action.payload.error);
    case 'CAMPAIGN.DELETE_FULFILLED':
      return moveRequestToFulfilled(state, 'CampaignDelete', action.payload.id);

    case 'CAMPAIGN.FOLLOW_PENDING':
      return moveRequestToPending(state, 'CampaignFollow', action.payload.id);
    case 'CAMPAIGN.FOLLOW_REJECTED':
      return moveRequestToRejected(state, 'CampaignFollow', action.payload.id, action.payload.error);
    case 'CAMPAIGN.FOLLOW_FULFILLED':
      return moveRequestToFulfilled(state, 'CampaignFollow', action.payload.id);

    case 'CAMPAIGN.UNFOLLOW_PENDING':
      return moveRequestToPending(state, 'CampaignUnfollow', action.payload.id);
    case 'CAMPAIGN.UNFOLLOW_REJECTED':
      return moveRequestToRejected(state, 'CampaignUnfollow', action.payload.id, action.payload.error);
    case 'CAMPAIGN.UNFOLLOW_FULFILLED':
      return moveRequestToFulfilled(state, 'CampaignUnfollow', action.payload.id);

    case 'CAMPAIGN.GOALTRACKER.CREATE_PENDING':
      return moveRequestToPending(state, 'CampaignGoalTrackerCreate', action.payload.id);
    case 'CAMPAIGN.GOALTRACKER.CREATE_REJECTED':
      return moveRequestToRejected(state, 'CampaignGoalTrackerCreate', action.payload.id, action.payload.error);
    case 'CAMPAIGN.GOALTRACKER.CREATE_FULFILLED':
      return moveRequestToFulfilled(state, 'CampaignGoalTrackerCreate', action.payload.id);

    case 'CAMPAIGN.GOALTRACKER.EDIT_PENDING':
      return moveRequestToPending(state, 'CampaignGoalTrackerEdit', action.payload.id);
    case 'CAMPAIGN.GOALTRACKER.EDIT_REJECTED':
      return moveRequestToRejected(state, 'CampaignGoalTrackerEdit', action.payload.id, action.payload.error);
    case 'CAMPAIGN.GOALTRACKER.EDIT_FULFILLED':
      return moveRequestToFulfilled(state, 'CampaignGoalTrackerEdit', action.payload.id);

    case 'GROUP_PINNED_RESOURCES.PIN_OBJECT_PENDING':
      return moveRequestToPending(state, 'PinnedResourcesPinLink', action.payload.groupId);
    case 'GROUP_PINNED_RESOURCES.PIN_OBJECT_REJECTED':
      return moveRequestToRejected(state, 'PinnedResourcesPinLink', action.payload.groupId, action.payload.error);
    case 'GROUP_PINNED_RESOURCES.PIN_OBJECT_FULFILLED':
      return moveRequestToFulfilled(state, 'PinnedResourcesPinLink', action.payload.groupId);

    case 'INBOX_UNREAD_COUNT.LOAD_PENDING':
      return moveRequestToPending(state, 'InboxUnreadCount', inboxUnreadCountRequestId);
    case 'INBOX_UNREAD_COUNT.LOAD_REJECTED':
      return moveRequestToRejected(state, 'InboxUnreadCount', inboxUnreadCountRequestId, action.payload.error);
    case 'INBOX_UNREAD_COUNT.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'InboxUnreadCount', inboxUnreadCountRequestId);

    case 'TOP_LEVEL_REPLIES.LOAD_INITIAL_PENDING':
      return moveRequestToPending(state, 'TopLevelRepliesLoadInitialReplies', action.payload.threadId);
    case 'TOP_LEVEL_REPLIES.LOAD_INITIAL_FULFILLED':
      return moveRequestToFulfilled(state, 'TopLevelRepliesLoadInitialReplies', action.payload.threadId);
    case 'TOP_LEVEL_REPLIES.LOAD_INITIAL_REJECTED':
      return moveRequestToRejected(
        state,
        'TopLevelRepliesLoadInitialReplies',
        action.payload.threadId,
        action.payload.error
      );

    case 'TOP_LEVEL_REPLIES.LOAD_LAST_PENDING':
      return moveRequestToPending(state, 'TopLevelRepliesLoadNext', action.payload.threadId);
    case 'TOP_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveRequestToFulfilled(state, 'TopLevelRepliesLoadNext', action.payload.threadId);
    case 'TOP_LEVEL_REPLIES.LOAD_LAST_REJECTED':
      return moveRequestToRejected(state, 'TopLevelRepliesLoadLast', action.payload.threadId, action.payload.error);

    case 'TOP_LEVEL_REPLIES.LOAD_NEXT_PENDING':
      return moveRequestToPending(state, 'TopLevelRepliesLoadNext', action.payload.threadId);
    case 'TOP_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveRequestToFulfilled(state, 'TopLevelRepliesLoadNext', action.payload.threadId);
    case 'TOP_LEVEL_REPLIES.LOAD_NEXT_REJECTED':
      return moveRequestToRejected(state, 'TopLevelRepliesLoadNext', action.payload.threadId, action.payload.error);
    case 'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_PENDING':
      return moveRequestToPending(state, 'TopLevelRepliesLoadPrevious', action.payload.threadId);
    case 'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveRequestToFulfilled(state, 'TopLevelRepliesLoadPrevious', action.payload.threadId);
    case 'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_REJECTED':
      return moveRequestToRejected(state, 'TopLevelRepliesLoadPrevious', action.payload.threadId, action.payload.error);

    case 'TOPIC.EDIT_PENDING':
      return moveRequestToPending(state, 'TopicEdit', action.payload.topicId);
    case 'TOPIC.EDIT_FULFILLED':
      return moveRequestToFulfilled(state, 'TopicEdit', action.payload.topicId);
    case 'TOPIC.EDIT_REJECTED':
      return moveRequestToRejected(
        state,
        'TopicEdit',
        action.payload.topicId,
        getGraphqlApiErrorReason(action.payload.error)
      );
    case 'TOPICS_ALL.LOAD_PENDING':
      return moveRequestToPending(state, 'TopicsAllLoad', topicsAllLoadRequestId);
    case 'TOPICS_ALL.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'TopicsAllLoad', topicsAllLoadRequestId);
    case 'TOPICS_ALL.LOAD_REJECTED':
      return moveRequestToRejected(
        state,
        'TopicsAllLoad',
        topicsAllLoadRequestId,
        getGraphqlApiErrorReason(action.payload.error)
      );
    case 'TOPICS_ALL.LOAD_MORE_PENDING':
      return moveRequestToPending(state, 'TopicsAllLoadMore', topicsAllLoadMoreRequestId);
    case 'TOPICS_ALL.LOAD_MORE_FULFILLED':
      return moveRequestToFulfilled(state, 'TopicsAllLoadMore', topicsAllLoadMoreRequestId);
    case 'TOPICS_ALL.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        'TopicsAllLoadMore',
        topicsAllLoadMoreRequestId,
        getGraphqlApiErrorReason(action.payload.error)
      );
    case 'TOPIC.UNCURATE_PENDING':
      return moveRequestToPending(state, 'TopicUncurate', action.payload.topicId);
    case 'TOPIC.UNCURATE_FULFILLED':
      return moveRequestToFulfilled(state, 'TopicUncurate', action.payload.topicId);
    case 'TOPIC.UNCURATE_REJECTED':
      return moveRequestToRejected(
        state,
        'TopicUncurate',
        action.payload.topicId,
        getGraphqlApiErrorReason(action.payload.error)
      );

    case 'SECOND_LEVEL_REPLIES.LOAD_NEXT_PENDING':
      return moveRequestToPending(state, 'SecondLevelRepliesLoadNext', action.payload.topLevelMessageId);
    case 'SECOND_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveRequestToFulfilled(state, 'SecondLevelRepliesLoadNext', action.payload.topLevelMessageId);
    case 'SECOND_LEVEL_REPLIES.LOAD_NEXT_REJECTED':
      return moveRequestToRejected(
        state,
        'SecondLevelRepliesLoadNext',
        action.payload.topLevelMessageId,
        action.payload.error
      );
    case 'SECOND_LEVEL_REPLIES.LOAD_PREVIOUS_PENDING':
      return moveRequestToPending(state, 'SecondLevelRepliesLoadPrevious', action.payload.topLevelMessageId);
    case 'SECOND_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveRequestToFulfilled(state, 'SecondLevelRepliesLoadPrevious', action.payload.topLevelMessageId);
    case 'SECOND_LEVEL_REPLIES.LOAD_PREVIOUS_REJECTED':
      return moveRequestToRejected(
        state,
        'SecondLevelRepliesLoadPrevious',
        action.payload.topLevelMessageId,
        action.payload.error
      );
    case 'SECOND_LEVEL_REPLIES.LOAD_LAST_PENDING':
      return moveRequestToPending(state, 'SecondLevelRepliesLoadLast', action.payload.topLevelMessageId);
    case 'SECOND_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveRequestToFulfilled(state, 'SecondLevelRepliesLoadLast', action.payload.topLevelMessageId);
    case 'SECOND_LEVEL_REPLIES.LOAD_LAST_REJECTED':
      return moveRequestToRejected(
        state,
        'SecondLevelRepliesLoadLast',
        action.payload.topLevelMessageId,
        action.payload.error
      );
    case 'SESSION.EDIT_CURRENT_NETWORK_USAGE_POLICY_PENDING':
      return moveRequestToPending(state, 'EditNetworkUsagePolicy', editNetworkUsagePolicyRequestId);
    case 'SESSION.EDIT_CURRENT_NETWORK_USAGE_POLICY_FULFILLED':
      return moveRequestToFulfilled(state, 'EditNetworkUsagePolicy', editNetworkUsagePolicyRequestId);
    case 'SESSION.EDIT_CURRENT_NETWORK_USAGE_POLICY_REJECTED':
      return moveRequestToRejected(
        state,
        'EditNetworkUsagePolicy',
        editNetworkUsagePolicyRequestId,
        action.payload.error
      );
    case 'SESSION.UPDATED_REACTION_ACCENT_COLOR_PENDING':
      return moveRequestToPending(state, 'ReactionAccentColorUpdate', updateReactionAccentColorRequestId);
    case 'SESSION.UPDATED_REACTION_ACCENT_COLOR_FULFILLED':
      return moveRequestToFulfilled(state, 'ReactionAccentColorUpdate', updateReactionAccentColorRequestId);
    case 'SESSION.UPDATED_REACTION_ACCENT_COLOR_REJECTED':
      return moveRequestToRejected(
        state,
        'ReactionAccentColorUpdate',
        updateReactionAccentColorRequestId,
        action.payload.error
      );

    case 'CHECK_SHAREPOINT_VIDEO_ACCESS.FULFILLED':
      return moveRequestToFulfilled(state, 'CheckSharePointVideoAccess', action.payload.id);
    case 'CHECK_SHAREPOINT_VIDEO_ACCESS.PENDING':
      return moveRequestToPending(state, 'CheckSharePointVideoAccess', action.payload.id);
    case 'CHECK_SHAREPOINT_VIDEO_ACCESS.REJECTED':
      return moveRequestToRejected(state, 'CheckSharePointVideoAccess', action.payload.id, action.payload.error);

    case 'SHAREPOINT_VIDEO_PREVIEW_URL.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'SharePointVideoPreviewUrlLoad', action.payload.id);
    case 'SHAREPOINT_VIDEO_PREVIEW_URL.LOAD_PENDING':
      return moveRequestToPending(state, 'SharePointVideoPreviewUrlLoad', action.payload.id);
    case 'SHAREPOINT_VIDEO_PREVIEW_URL.LOAD_REJECTED':
      return moveRequestToRejected(state, 'SharePointVideoPreviewUrlLoad', action.payload.id, action.payload.error);

    case 'WEBLINK.RESOLVE_FULFILLED':
      return moveRequestToFulfilled(state, 'WebLinkResolve', action.payload.link);
    case 'WEBLINK.RESOLVE_PENDING':
      return moveRequestToPending(state, 'WebLinkResolve', action.payload.link);
    case 'WEBLINK.RESOLVE_REJECTED':
      return moveRequestToRejected(state, 'WebLinkResolve', action.payload.link, action.payload.error);

    case 'LIVE_EVENT.DOWNLOAD_ICS_FILE_PENDING':
      return moveRequestToPending(state, 'DownloadIcs', action.payload.broadcastId);
    case 'LIVE_EVENT.DOWNLOAD_ICS_FILE_FULFILLED':
      return moveRequestToFulfilled(state, 'DownloadIcs', action.payload.broadcastId);
    case 'LIVE_EVENT.DOWNLOAD_ICS_FILE_REJECTED':
      return moveRequestToRejected(state, 'DownloadIcs', action.payload.broadcastId, action.payload.error);

    case 'NESTED_THREAD_LIST.LOAD_PENDING':
      return moveRequestToPending(state, 'ThreadListLoad', action.payload.threadListId);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadListLoad', action.payload.threadListId);
    case 'NESTED_THREAD_LIST.LOAD_REJECTED':
      return moveRequestToRejected(state, 'ThreadListLoad', action.payload.threadListId, action.payload.error);

    case 'THREAD_SHARES.LOAD_PENDING':
      return moveRequestToPending(state, 'ThreadShareLoad', action.payload.threadId);
    case 'THREAD_SHARES.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'ThreadShareLoad', action.payload.threadId);
    case 'THREAD_SHARES.LOAD_REJECTED':
      return moveRequestToRejected(state, 'ThreadShareLoad', action.payload.threadId, action.payload.error);

    case 'MY_GROUPS.LOAD_MORE_PENDING':
      return moveRequestToPending(state, 'MyGroupsLoadMore', `${action.payload.filteredBy}`);
    case 'MY_GROUPS.LOAD_MORE_FULFILLED':
      return removeRequest(state, 'MyGroupsLoadMore', `${action.payload.filteredBy}`);
    case 'MY_GROUPS.LOAD_MORE_REJECTED':
      return moveRequestToRejected(state, 'MyGroupsLoadMore', `${action.payload.filteredBy}`, action.payload.error);

    case 'TOPICS_FOLLOWED.LOAD_MORE_PENDING':
      return moveRequestToPending(state, 'TopicsFollowedLoadMore', action.topicsFollowedRequestStatusKey);
    case 'TOPICS_FOLLOWED.LOAD_MORE_FULFILLED':
      return removeRequest(state, 'TopicsFollowedLoadMore', action.topicsFollowedRequestStatusKey);
    case 'TOPICS_FOLLOWED.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        'TopicsFollowedLoadMore',
        action.topicsFollowedRequestStatusKey,
        action.payload.error
      );

    case 'TRENDING_TOPICS.LOAD_MORE_PENDING':
      return moveRequestToPending(state, 'TrendingTopicsLoadMore', action.trendingTopicsRequestStatusKey);
    case 'TRENDING_TOPICS.LOAD_MORE_FULFILLED':
      return removeRequest(state, 'TrendingTopicsLoadMore', action.trendingTopicsRequestStatusKey);
    case 'TRENDING_TOPICS.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        'TrendingTopicsLoadMore',
        action.trendingTopicsRequestStatusKey,
        action.payload.error
      );
    case 'ALL_LEADERS_IN_USERS_NETWORK.LOAD_FULFILLED':
      return removeRevokeLeaderRequestsForLoadedLeaders(state, action);
    case 'ALL_LEADERS_IN_USERS_NETWORK.LOAD_MORE_PENDING':
      return moveRequestToPending(state, 'UserNetworkLeadersListMore', action.userNetworkLeadersListRequestStatusKey);
    case 'ALL_LEADERS_IN_USERS_NETWORK.LOAD_MORE_FULFILLED':
      return removeRequest(
        removeRevokeLeaderRequestsForLoadedLeaders(state, action),
        'UserNetworkLeadersListMore',
        action.userNetworkLeadersListRequestStatusKey
      );
    case 'ALL_LEADERS_IN_USERS_NETWORK.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        'UserNetworkLeadersListMore',
        action.userNetworkLeadersListRequestStatusKey,
        action.payload.error
      );
    case 'ALL_CAMPAIGNS.LOAD_MORE_PENDING':
      return moveRequestToPending(state, 'CampaignsListMore', action.campaignsListRequestStatusKey);
    case 'ALL_CAMPAIGNS.LOAD_MORE_FULFILLED':
      return removeRequest(state, 'CampaignsListMore', action.campaignsListRequestStatusKey);
    case 'ALL_CAMPAIGNS.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        'CampaignsListMore',
        action.campaignsListRequestStatusKey,
        action.payload.error
      );
    case 'UPDATE_HAS_NETWORK_WIDE_AUDIENCE.PENDING':
      return moveRequestToPending(state, 'UpdateHasNetworkWideAudience', action.payload.userId);
    case 'UPDATE_HAS_NETWORK_WIDE_AUDIENCE.FULFILLED':
      return moveRequestToFulfilled(state, 'UpdateHasNetworkWideAudience', action.payload.userId);
    case 'UPDATE_HAS_NETWORK_WIDE_AUDIENCE.REJECTED':
      return moveRequestToRejected(state, 'UpdateHasNetworkWideAudience', action.payload.userId, action.payload.error);
    case 'UPDATE_HAS_MULTI_TENANT_WIDE_AUDIENCE.PENDING':
      return moveRequestToPending(state, 'UpdateHasMultiTenantWideAudience', action.payload.userId);
    case 'UPDATE_HAS_MULTI_TENANT_WIDE_AUDIENCE.FULFILLED':
      return moveRequestToFulfilled(state, 'UpdateHasMultiTenantWideAudience', action.payload.userId);
    case 'UPDATE_HAS_MULTI_TENANT_WIDE_AUDIENCE.REJECTED':
      return moveRequestToRejected(
        state,
        'UpdateHasMultiTenantWideAudience',
        action.payload.userId,
        action.payload.error
      );

    case 'CONNECTED_LEADERS.LOAD_MORE_PENDING':
      return moveRequestToPending(
        state,
        'UserConnectedLeadersListLoadMore',
        action.userConnectedLeadersListRequestStatusKey
      );
    case 'CONNECTED_LEADERS.LOAD_MORE_FULFILLED':
      return removeRequest(state, 'UserConnectedLeadersListLoadMore', action.userConnectedLeadersListRequestStatusKey);
    case 'CONNECTED_LEADERS.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        'UserConnectedLeadersListLoadMore',
        action.userConnectedLeadersListRequestStatusKey,
        action.payload.error
      );

    case 'FOLLOWED_LEADERS.LOAD_MORE_PENDING':
      return moveRequestToPending(
        state,
        'UserFollowedLeadersListLoadMore',
        action.userFollowedLeadersListRequestStatusKey
      );
    case 'FOLLOWED_LEADERS.LOAD_MORE_FULFILLED':
      return removeRequest(state, 'UserFollowedLeadersListLoadMore', action.userFollowedLeadersListRequestStatusKey);
    case 'FOLLOWED_LEADERS.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        'UserFollowedLeadersListLoadMore',
        action.userFollowedLeadersListRequestStatusKey,
        action.payload.error
      );

    case 'ADMIN_ROLES.UPDATE_ROLES_PENDING':
      return moveRequestToPending(state, 'UpdateUserAdminRoles', action.payload.userId);
    case 'ADMIN_ROLES.UPDATE_ROLES_FULFILLED':
      return moveRequestToFulfilled(state, 'UpdateUserAdminRoles', action.payload.userId);
    case 'ADMIN_ROLES.UPDATE_ROLES_REJECTED':
      return moveRequestToRejected(state, 'UpdateUserAdminRoles', action.payload.userId, action.payload.error);
    case 'STORYLINE_ADMIN_SETTINGS.UPDATE_DEFAULT_NOTIFICATION_DELIVERY_SETTINGS_PENDING':
      return moveRequestToPending(
        state,
        'UpdateStorylineDefaultNotificationDeliverySettings',
        updateStorylineDefaultNotificationDeliverySettingsRequestId
      );
    case 'STORYLINE_ADMIN_SETTINGS.UPDATE_DEFAULT_NOTIFICATION_DELIVERY_SETTINGS_FULFILLED':
      return moveRequestToFulfilled(
        state,
        'UpdateStorylineDefaultNotificationDeliverySettings',
        updateStorylineDefaultNotificationDeliverySettingsRequestId
      );
    case 'STORYLINE_ADMIN_SETTINGS.UPDATE_DEFAULT_NOTIFICATION_DELIVERY_SETTINGS_REJECTED':
      return moveRequestToRejected(
        state,
        'UpdateStorylineDefaultNotificationDeliverySettings',
        updateStorylineDefaultNotificationDeliverySettingsRequestId,
        action.payload.error
      );

    case 'STORYLINE_ADMIN_SETTINGS.UPDATE_PER_USER_CONTROL_VALUES_PENDING':
      return moveRequestToPending(
        state,
        'UpdateStorylinePerUserControlSettings',
        updateStorylinePerUserControlSettingsRequestId
      );
    case 'STORYLINE_ADMIN_SETTINGS.UPDATE_PER_USER_CONTROL_VALUES_FULFILLED':
      return moveRequestToFulfilled(
        state,
        'UpdateStorylinePerUserControlSettings',
        updateStorylinePerUserControlSettingsRequestId
      );
    case 'STORYLINE_ADMIN_SETTINGS.UPDATE_PER_USER_CONTROL_VALUES_REJECTED':
      return moveRequestToRejected(
        state,
        'UpdateStorylinePerUserControlSettings',
        updateStorylinePerUserControlSettingsRequestId,
        action.payload.error
      );

    case 'STORYLINE_ADMIN_SETTINGS.UPDATE_MTO_ADMIN_SETTINGS_PENDING':
      return moveRequestToPending(state, 'UpdateStorylineMTOAdminSettings', updateStorylineMtoAdminSettingsRequestId);
    case 'STORYLINE_ADMIN_SETTINGS.UPDATE_MTO_ADMIN_SETTINGS_FULFILLED':
      return moveRequestToFulfilled(state, 'UpdateStorylineMTOAdminSettings', updateStorylineMtoAdminSettingsRequestId);
    case 'STORYLINE_ADMIN_SETTINGS.UPDATE_MTO_ADMIN_SETTINGS_REJECTED':
      return moveRequestToRejected(
        state,
        'UpdateStorylineMTOAdminSettings',
        updateStorylineMtoAdminSettingsRequestId,
        action.payload.error
      );

    case 'INFORMATION_BARRIER_SETTINGS.EDIT_PENDING':
      return moveRequestToPending(
        state,
        'UpdateInformationBarrierAdminSettings',
        editInformationBarrierAdminSettingsRequestId
      );
    case 'INFORMATION_BARRIER_SETTINGS.EDIT_FULFILLED':
      return moveRequestToFulfilled(
        state,
        'UpdateInformationBarrierAdminSettings',
        editInformationBarrierAdminSettingsRequestId
      );
    case 'INFORMATION_BARRIER_SETTINGS.EDIT_REJECTED':
      return moveRequestToRejected(
        state,
        'UpdateInformationBarrierAdminSettings',
        editInformationBarrierAdminSettingsRequestId,
        true
      );

    case 'DESIGNATE_LEADERS_FROM_OFFICE_GROUP_ASYNC.PENDING':
      return moveRequestToPending(state, 'DesignateLeadersFromOfficeGroupAsyncAction', action.payload.officeGroupId);
    case 'DESIGNATE_LEADERS_FROM_OFFICE_GROUP_ASYNC.FULFILLED':
      return moveRequestToFulfilled(state, 'DesignateLeadersFromOfficeGroupAsyncAction', action.payload.officeGroupId);
    case 'DESIGNATE_LEADERS_FROM_OFFICE_GROUP_ASYNC.REJECTED':
      return moveRequestToRejected(
        state,
        'DesignateLeadersFromOfficeGroupAsyncAction',
        action.payload.officeGroupId,
        action.payload.error
      );

    case 'SUGGESTED_TOPICS.LOAD_MORE_PENDING':
      return moveRequestToPending(state, 'SuggestedTopicsLoadMore', suggestedTopicsLoadMoreRequestId);
    case 'SUGGESTED_TOPICS.LOAD_MORE_FULFILLED':
      return removeRequest(state, 'SuggestedTopicsLoadMore', suggestedTopicsLoadMoreRequestId);
    case 'SUGGESTED_TOPICS.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        'SuggestedTopicsLoadMore',
        suggestedTopicsLoadMoreRequestId,
        action.payload.error
      );

    case 'CREATE_CALENDAR_INVITE_FOR_TEAMS_MEETING.PENDING':
      return moveRequestToPending(state, 'CreateCalendarInviteForTeamsMeeting', action.payload.teamsMeetingId);
    case 'CREATE_CALENDAR_INVITE_FOR_TEAMS_MEETING.FULFILLED':
      return moveRequestToFulfilled(state, 'CreateCalendarInviteForTeamsMeeting', action.payload.teamsMeetingId);
    case 'CREATE_CALENDAR_INVITE_FOR_TEAMS_MEETING.REJECTED':
      return moveRequestToRejected(
        state,
        'CreateCalendarInviteForTeamsMeeting',
        action.payload.teamsMeetingId,
        action.payload.error
      );
    case 'TYPEAHEAD.LOAD_FULFILLED':
      return removeRevokeLeaderRequestsForLoadedLeaders(state, action);
    case 'LEADER.LOAD_FULFILLED':
      return removeRevokeLeaderRequestsForLoadedLeaders(state, action);
    case 'OFFICIAL_GROUPS.LOAD_MORE_PENDING':
      return moveRequestToPending(state, 'OfficialGroupsLoadMore', officialGroupsLoadMoreRequestId);
    case 'OFFICIAL_GROUPS.LOAD_MORE_FULFILLED':
      return removeRequest(state, 'OfficialGroupsLoadMore', officialGroupsLoadMoreRequestId);
    case 'OFFICIAL_GROUPS.LOAD_MORE_REJECTED':
      return moveRequestToRejected(
        state,
        'OfficialGroupsLoadMore',
        officialGroupsLoadMoreRequestId,
        action.payload.error
      );
    case 'CREATE_AUDIENCE.PENDING':
      return moveRequestToPending(state, 'CreateAudience', action.payload.userId);
    case 'CREATE_AUDIENCE.FULFILLED':
      return moveRequestToFulfilled(state, 'CreateAudience', action.payload.userId);
    case 'CREATE_AUDIENCE.REJECTED':
      return moveRequestToRejected(state, 'CreateAudience', action.payload.userId, action.payload.error);
    case 'CREATE_AUDIENCE_FROM_OFFICE_GROUP.PENDING':
      return moveRequestToPending(state, 'CreateAudienceFromOfficeGroup', action.payload.userId);
    case 'CREATE_AUDIENCE_FROM_OFFICE_GROUP.FULFILLED':
      return moveRequestToFulfilled(state, 'CreateAudienceFromOfficeGroup', action.payload.userId);
    case 'CREATE_AUDIENCE_FROM_OFFICE_GROUP.REJECTED':
      return moveRequestToRejected(state, 'CreateAudienceFromOfficeGroup', action.payload.userId, action.payload.error);
    case 'EDIT_AUDIENCE.PENDING':
      return moveRequestToPending(state, 'EditAudience', action.payload.audienceId);
    case 'EDIT_AUDIENCE.FULFILLED':
      return moveRequestToFulfilled(state, 'EditAudience', action.payload.audienceId);
    case 'EDIT_AUDIENCE.REJECTED':
      return moveRequestToRejected(state, 'EditAudience', action.payload.audienceId, action.payload.error);
    case 'REMOVE_LEADER_AUDIENCE.PENDING':
      return moveRequestToPending(state, 'RemoveLeaderAudience', action.payload.audienceId);
    case 'REMOVE_LEADER_AUDIENCE.FULFILLED':
      return moveRequestToFulfilled(state, 'RemoveLeaderAudience', action.payload.audienceId);
    case 'REMOVE_LEADER_AUDIENCE.REJECTED':
      return moveRequestToRejected(state, 'RemoveLeaderAudience', action.payload.audienceId, action.payload.error);
    case 'THREADS_APPLIED_TOPIC_NAMES.LOAD_REJECTED':
      return moveRequestToRejected(state, 'ThreadsAppliedTopicNames', action.payload.id, action.payload.error);
    case 'CREATE_AUDIENCE_FROM_DIRECT_MEMBERS.PENDING':
      return moveRequestToPending(state, 'CreateAudienceFromDirectMembers', action.payload.userId);
    case 'CREATE_AUDIENCE_FROM_DIRECT_MEMBERS.FULFILLED':
      return moveRequestToFulfilled(state, 'CreateAudienceFromDirectMembers', action.payload.userId);
    case 'CREATE_AUDIENCE_FROM_DIRECT_MEMBERS.REJECTED':
      return moveRequestToRejected(
        state,
        'CreateAudienceFromDirectMembers',
        action.payload.userId,
        action.payload.error
      );
    case 'LEADER_AUDIENCES.MARK_AUDIENCE_AS_DEFAULT_PENDING':
      return moveRequestToPending(state, 'MarkUserDefaultAudience', action.payload.userId);
    case 'LEADER_AUDIENCES.MARK_AUDIENCE_AS_DEFAULT_FULFILLED':
      return moveRequestToFulfilled(state, 'MarkUserDefaultAudience', action.payload.userId);
    case 'LEADER_AUDIENCES.MARK_AUDIENCE_AS_DEFAULT_REJECTED':
      return moveRequestToRejected(state, 'MarkUserDefaultAudience', action.payload.userId, action.payload.error);

    case 'UPLOAD_TENANT_LOGO.PENDING':
      return moveRequestToPending(state, 'UploadTenantLogo', uploadTenantLogoRequestId);
    case 'UPLOAD_TENANT_LOGO.FULFILLED':
      return moveRequestToFulfilled(state, 'UploadTenantLogo', uploadTenantLogoRequestId);
    case 'UPLOAD_TENANT_LOGO.REJECTED':
      return moveRequestToRejected(state, 'UploadTenantLogo', uploadTenantLogoRequestId, action.payload.error);
    case 'TOPIC.REMOVE_PENDING':
      return moveRequestToPending(state, 'RemoveTopic', action.payload.topicId);
    case 'TOPIC.REMOVE_FULFILLED':
      return moveRequestToFulfilled(state, 'RemoveTopic', action.payload.topicId);
    case 'TOPIC.REMOVE_REJECTED':
      return moveRequestToRejected(
        state,
        'RemoveTopic',
        action.payload.topicId,
        getGraphqlApiErrorReason(action.payload.error)
      );

    case 'GROUP.MARK_AS_MTO_GROUP_PENDING':
      return moveRequestToPending(state, 'MarkMultiTenantOrganizationGroup', action.payload.groupId);
    case 'GROUP.MARK_AS_MTO_GROUP_FULFILLED':
      return moveRequestToFulfilled(state, 'MarkMultiTenantOrganizationGroup', action.payload.groupId);
    case 'GROUP.MARK_AS_MTO_GROUP_REJECTED':
      return moveRequestToRejected(
        state,
        'MarkMultiTenantOrganizationGroup',
        action.payload.groupId,
        action.payload.error
      );
    case 'GROUP.UNMARK_AS_MTO_GROUP_PENDING':
      return moveRequestToPending(state, 'UnmarkMultiTenantOrganizationGroup', action.payload.groupId);
    case 'GROUP.UNMARK_AS_MTO_GROUP_FULFILLED':
      return moveRequestToFulfilled(state, 'UnmarkMultiTenantOrganizationGroup', action.payload.groupId);
    case 'GROUP.UNMARK_AS_MTO_GROUP_REJECTED':
      return moveRequestToRejected(
        state,
        'UnmarkMultiTenantOrganizationGroup',
        action.payload.groupId,
        action.payload.error
      );
    case 'ROLE_BASED_FRE.LOAD_TENANT_CONFIGURATION_AND_ROLES_PENDING':
      return moveRequestToPending(state, 'TenantConfigurationAndRoles', tenantConfigurationAndRolesRequestId);
    case 'ROLE_BASED_FRE.LOAD_TENANT_CONFIGURATION_AND_ROLES_REJECTED':
      return moveRequestToRejected(
        state,
        'TenantConfigurationAndRoles',
        tenantConfigurationAndRolesRequestId,
        action.payload.error
      );
    case 'ROLE_BASED_FRE.LOAD_TENANT_CONFIGURATION_AND_ROLES_FULFILLED':
      return moveRequestToFulfilled(state, 'TenantConfigurationAndRoles', tenantConfigurationAndRolesRequestId);
    case 'GROUP.ADD_LEADER_PENDING':
      return moveRequestToPending(
        removeRequest(state, 'GroupLeaderAddRemove', action.payload.groupId),
        'GroupLeaderAdd',
        `${action.payload.groupId}-${action.payload.userId}`
      );
    case 'GROUP.ADD_LEADER_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupLeaderAdd', `${action.payload.groupId}-${action.payload.userId}`);
    case 'GROUP.ADD_LEADER_REJECTED':
      return moveToRejected(
        state,
        [
          getId('GroupLeaderAddRemove', action.payload.groupId),
          getId('GroupLeaderAdd', `${action.payload.groupId}-${action.payload.userId}`),
        ],
        action.payload.error
      );
    case 'GROUP.REMOVE_LEADER_PENDING':
      return moveRequestToPending(
        removeRequest(state, 'GroupLeaderAddRemove', action.payload.groupId),
        'GroupLeaderRemove',
        `${action.payload.groupId}-${action.payload.userId}`
      );
    case 'GROUP.REMOVE_LEADER_FULFILLED':
      return moveRequestToFulfilled(state, 'GroupLeaderRemove', `${action.payload.groupId}-${action.payload.userId}`);
    case 'GROUP.REMOVE_LEADER_REJECTED':
      return moveToRejected(
        state,
        [
          getId('GroupLeaderAddRemove', action.payload.groupId),
          getId('GroupLeaderRemove', `${action.payload.groupId}-${action.payload.userId}`),
        ],
        action.payload.error
      );
    case 'M365_COPILOT_SUGGESTED_MEMBERS.ADD_ALL_PENDING':
      return moveRequestToPending(state, 'M365CopilotGroupSuggestedMembersBulkAdd', action.payload.id);
    case 'M365_COPILOT_SUGGESTED_MEMBERS.ADD_ALL_FULFILLED':
      return moveRequestToFulfilled(state, 'M365CopilotGroupSuggestedMembersBulkAdd', action.payload.id);
    case 'M365_COPILOT_SUGGESTED_MEMBERS.ADD_ALL_REJECTED':
      return moveRequestToRejected(
        state,
        'M365CopilotGroupSuggestedMembersBulkAdd',
        action.payload.id,
        action.payload.error
      );

    case 'USER.HIDE_USER_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'UserHideUser',
        removeType: 'UserUnhideUser',
        entityId: action.payload.userId,
      });
    case 'USER.HIDE_USER_FULFILLED':
      return moveRequestToFulfilled(state, 'UserHideUser', action.payload.userId);
    case 'USER.HIDE_USER_REJECTED':
      return moveRequestToRejected(state, 'UserHideUser', action.payload.userId, action.payload.error);
    case 'USER.UNHIDE_USER_PENDING':
      return moveRequestToPendingAndRemoveAssociatedRequest(state, {
        pendingType: 'UserUnhideUser',
        removeType: 'UserHideUser',
        entityId: action.payload.userId,
      });
    case 'USER.UNHIDE_USER_FULFILLED':
      return moveRequestToFulfilled(state, 'UserUnhideUser', action.payload.userId);
    case 'USER.UNHIDE_USER_REJECTED':
      return moveRequestToRejected(state, 'UserUnhideUser', action.payload.userId, action.payload.error);

    case 'USER_PREFERENCES.UPDATE_USER_PREFERENCES_PENDING':
      return moveRequestToPending(state, 'UpdateUserPreferences', action.payload.id);
    case 'USER_PREFERENCES.UPDATE_USER_PREFERENCES_FULFILLED':
      return moveRequestToFulfilled(state, 'UpdateUserPreferences', action.payload.id);
    case 'USER_PREFERENCES.UPDATE_USER_PREFERENCES_REJECTED':
      return moveRequestToRejected(state, 'UpdateUserPreferences', action.payload.id, action.payload.error);
    case 'USER_INFORMATION_BARRIER_SETTINGS.LOAD_PENDING':
      return moveRequestToPending(state, 'UserInformationBarrierSettings', action.payload.id);
    case 'USER_INFORMATION_BARRIER_SETTINGS.LOAD_REJECTED':
      return moveRequestToRejected(state, 'UserInformationBarrierSettings', action.payload.id, action.payload.error);
    case 'USER_INFORMATION_BARRIER_SETTINGS.LOAD_FULFILLED':
      return moveRequestToFulfilled(state, 'UserInformationBarrierSettings', action.payload.id);
    case 'TOPIC_FOLLOWERS.LOAD_MORE_PENDING':
      return moveRequestToPending(state, 'TopicFollowersPagination', action.payload.topicId);
    case 'TOPIC_FOLLOWERS.LOAD_MORE_FULFILLED':
      return moveRequestToFulfilled(state, 'TopicFollowersPagination', action.payload.topicId);
    case 'TOPIC_FOLLOWERS.LOAD_MORE_REJECTED':
      return moveRequestToRejected(state, 'TopicFollowersPagination', action.payload.topicId, action.payload.error);
    default:
      return state;
  }
};

export default reducer;
